import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFlow } from "@/contexts/FlowContext";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CheckCircle2, ArrowRight, Shield, Briefcase, Globe, UserPlus, FileCheck,
  Link2, Plus, Check, RotateCcw, ChevronDown, ChevronRight, ShieldCheck, X,
  LogOut, Loader2, Mail, Phone, AlertTriangle, ExternalLink, Trash2, Send, ArrowLeft,
  Lock, Sparkles, Star, Clock, ReceiptText, Github,
} from "lucide-react";
import type { ConnectedSource } from "@/contexts/FlowContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import ResumeGate from "@/components/ResumeGate";
import { useStepParam } from "@/hooks/useStepParam";
import { ComingSoonBadge } from "@/components/ComingSoonBadge";
import { initClarity } from "@/lib/clarity";
import { relationshipOptionsFor } from "@/lib/relationshipOptions";
import { usePublishSessionVertical } from "@/lib/sessionVertical";
import { isPartnerLookKey, usesPartnerLook } from "@/lib/flowPresentation";
import { copyForIndustry, isRecruiting as isRecruitingVertical, verticalFrom } from "@/lib/verticalCopy";
import { EmailCodePanel } from "@/components/EmailCodePanel";
import { Checkbox } from "@/components/ui/checkbox";
import { useSessionPolling } from "@/hooks/useSessionPolling";
import { useVoucherInvitations } from "@/hooks/useVoucherInvitations";
import { applicantApi, apiBaseConfigured, type ApiError, type SignalSummary, type SessionStatus, type VoucherInvitation } from "@/lib/api";
import { getSubmissionState, SUBMISSION_REJECTED_MESSAGE, countConnectedOAuth, requiredVouchesFor, isOpenVouchInvite } from "@/lib/submissionEligibility";
import {
  flowSubmissionRule,
  serverSubmissionRule,
  requiredVouchInvites,
  isSubmitReady,
  showsTrackTracker,
  offersAccountsForFewerVouches,
  promotesSkippedSteps,
  outstandingBeforeSubmit,
  applicationEmailCard,
  applicationEmailStepSatisfied,
  continueBlockedMessage,
} from "@/lib/applicantRequirements";
import { offerableSources } from "@/lib/offerableSources";
import { oauthProviderKeyName, oauthProviderLabel } from "@/lib/providerLabel";
import { classifyEmail, unconnectedEmailProviderOptions } from "@/lib/emailRouting";
import {
  anySignInProvedEmail,
  connectionProvedEmail,
  isOtpEmailSignal,
  signInProvedEmail,
} from "@/lib/emailProof";

/** Demo tiles that stand in for a sign-in which proves the address. */
const DEMO_EMAIL_PROVING_TILES = new Set(["google", "microsoft", "linkedin", "apple"]);
import { isSelfInvite, SELF_INVITE_MESSAGE } from "@/lib/selfInvite";
import { voucherInviteErrorToast } from "@/lib/voucherInviteErrors";
import {
  APPLICANT_STEP,
  buildDefinitionPlan,
  definitionEvidenceByIndex,
  frontierIndexInPlan,
  isIdvSatisfied,
  isLastInPlan,
  laterInPlan,
  nextIndexInPlan,
  offersSkipStep,
  rankOfIndex,
  stepCounter,
  type FlowPlanEntry,
} from "@/lib/flowPlan";
import { demoAvailableOAuth, demoFlowFromParam, DEMO_LEASING_ORG, PREVIEW_ROUTE_NOTE, type DemoFlow } from "@/lib/demoFlows";
import { DemoEmailCodePanel, DemoIDVPanel } from "@/components/DemoVerificationPanels";
import { toast } from "@/hooks/use-toast";
import { IS_STAGING, SHOW_FINANCIAL_ACCOUNTS, SHOW_PAYROLL_DOC_FALLBACK } from "@/lib/env";
import { trackPayrollFunnel } from "@/lib/payrollFunnel";
import { toast as sonnerToast } from "sonner";
import { PlaidLinkButton } from "@/components/PlaidLinkButton";
import { ApplicantIDVCard } from "@/components/ApplicantIDVCard";
import ApplicantFlowStep1New from "@/pages/ApplicantFlowStep1New";
import { AccountChecksTracker } from "@/components/AccountChecksTracker";
import { liveCompletionScreen } from "@/lib/completionScreen";
import { BrandIcon, resolveBrandKey } from "@/components/BrandIcon";
import { useTenant } from "@/hooks/useTenant";
import { FEATHER_FEATURED_ACCOUNT_IDS, FEATHER_OTHER_ACCOUNT_IDS } from "@/lib/featherIntegration";
import { applicantConsentSentence } from "@/lib/applicantConsent";

// Step `hidden` flag suppresses the step from the stepper, navigation, and
// step-content rendering. Underlying indices stay stable so existing `current
// === N` conditionals don't shift — flipping `hidden: false` re-enables a step
// with zero other code changes.
//
// Hidden as of 2026-05-20:
//   - index 3 ("Connect a work or income source") — no backend integration yet.
//     Re-enable when payroll/bank connectors ship.
const steps = [
  { title: "Confirm identity details", icon: Shield, desc: "Review and confirm the information you've already provided." },
  { title: "Verify your email", icon: Mail, desc: "Confirm the email on your application is yours." },
  { title: "Add more supporting connections", icon: Link2, desc: "If helpful, you can connect additional accounts to strengthen your application." },
  { title: "Connect a work or income source", icon: Briefcase, desc: "Optional — this helps confirm income continuity and account ownership.", optional: true, hidden: true },
  { title: "Invite someone who knows you", icon: UserPlus, desc: "Invite people who can confirm they know you." },
  { title: "Review and submit", icon: FileCheck, desc: "Review all the information you've provided before submitting." },
  // Index 6 — Plaid IDV as a screen of its own. Reachable ONLY from a
  // definition-driven flow (ADR-014): it is `hidden` here, so
  // `visibleStepIndices` is unchanged at [0, 1, 2, 4, 5] and the leasing
  // journey cannot land on it. Appended rather than slotted in after consent
  // because the indices are the `current === N` keys and renumbering them
  // would move every content block in this file.
  //
  // Not the same thing as the IDV card on step 1, which is gated on the org
  // flag `applicant_idv_required` (ADR-012, asked-for-never-enforced). That
  // card stays exactly where it is; this screen is the definition naming
  // `plaid_idv` as an ordered step.
  { title: "Verify your ID", icon: ShieldCheck, desc: "Scan a government ID and take a quick selfie.", hidden: true },
];

// Array of step indices in display order, omitting hidden steps.
// e.g. with index 3 hidden → [0, 1, 2, 4, 5].
const visibleStepIndices = steps
  .map((s, i) => ({ idx: i, hidden: (s as { hidden?: boolean }).hidden ?? false }))
  .filter((s) => !s.hidden)
  .map((s) => s.idx);

// The legacy plan: the hardcoded flow, unchanged, as a list of FlowPlanEntry.
// This is the ONLY plan a session without a workflow_key ever renders, and it
// is byte-identical to what `visibleStepIndices` + `steps[i]` produced before —
// same order, same titles, same icons, same count.
const LEGACY_PLAN: FlowPlanEntry[] = visibleStepIndices.map((i) => ({
  index: i,
  title: steps[i].title,
  desc: steps[i].desc,
  icon: steps[i].icon,
  optional: (steps[i] as { optional?: boolean }).optional,
}));

type AccountTile = {
  name: string;
  desc: string;
  provider?: "google" | "microsoft" | "linkedin" | "apple";
  comingSoon?: boolean;
  /**
   * Render only when the backend advertises this provider in
   * `available_oauth_providers` (BACKEND_INTEGRATION.md §26).
   *
   * Opt-in per tile rather than applied to all of them: providers that predate
   * that field must keep rendering unconditionally, or an older backend (which
   * normalizes to an empty list) would blank the whole step.
   */
  gatedOnAvailability?: boolean;
};

// Page 2 — Digital account providers.
//
// Hidden providers are commented out below (not removed) so re-enabling is a
// one-line uncomment. See docs/providers/IMPLEMENTATION_BACKLOG.md for what
// each one needs to ship.
//
// Page 2 ("primary digital account") = email/identity providers that write
// digital.email_history. Amazon is on Page 3 even though it also writes
// digital.email_history — that's a UX choice ("primary identity first, optional
// later"), not a signal-category statement. See docs/SCORING.md in the
// digavouch-platform repo for the full rationale.
//
// Hidden as of 2026-05-21:
//   - Apple       (Page 2 → needs developer account + OAuth backend)
//
// Tenant note for Microsoft: backend uses /common/oauth2/v2.0 so both
// work/school and personal Microsoft accounts can sign in. The Azure app
// registration must be configured with "Accounts in any organizational
// directory and personal Microsoft accounts" to match.
const accountOptions: AccountTile[] = [
  { name: "Google", desc: "Confirm your Google email belongs to you.", provider: "google" },
  { name: "Microsoft / Outlook / Hotmail / Live", desc: "Use Outlook, Hotmail, Live, or another Microsoft email account to verify email ownership.", provider: "microsoft" },
  // Hidden until the backend reports Apple credentials for this environment —
  // no build-time flag to keep in sync. Deliberately doesn't promise a
  // particular address: Apple's "Hide My Email" returns a relay alias.
  { name: "Apple", desc: "Use your Apple ID to verify email ownership.", provider: "apple", gatedOnAvailability: true },
];

interface SourceOption {
  id: string;
  name: string;
  desc: string;
  category: "social" | "financial" | "other";
  /**
   * Static placeholder text shown nowhere to a live applicant; the demo copies
   * it into its local connected-source state. A tile whose real signal is
   * age-graded (GitHub) omits both: a fixed "7 years" beside a real age label
   * would read as a claim about THIS applicant's account.
   */
  tenure?: string;
  corroboration?: string;
  strength: "strong" | "moderate" | "limited";
  recommended?: boolean;
  tag?: string;
  // Live wiring
  liveOAuth?: "google" | "microsoft" | "apple" | "linkedin" | "amazon" | "uber" | "facebook" | "github"; // OAuth flow key
  comingSoon?: boolean;
  /**
   * Render only when the backend advertises this provider in
   * `available_oauth_providers` (BACKEND_INTEGRATION.md §26).
   *
   * Opt-in per tile, exactly as `AccountTile.gatedOnAvailability` is: providers
   * that predate that field keep rendering unconditionally, or an older backend
   * (which normalizes to an empty list) would blank the whole step.
   *
   * Facebook is the first source here to use it, and it is what lets this tile
   * ship BEFORE `FACEBOOK_CLIENT_ID` is set in an environment. Without it, a tile
   * would appear that `POST /verify/oauth/facebook` answers with "Facebook OAuth
   * not configured" — a dead end the applicant cannot act on. With it, the tile
   * simply appears on its own once the credentials land, with no further deploy.
   */
  gatedOnAvailability?: boolean;
  /**
   * Do not offer this tile on a definition-driven plan whose digital-account
   * step is REQUIRED and does not list `oauth_<liveOAuth>` in `satisfied_by`:
   * the tile could not complete the step. See `@/lib/offerableSources`.
   */
  hideWhenRequiredStepCannotUseIt?: boolean;
}

// Page 3 — Supporting connections (social + other corroboration).
//
// Hidden providers are commented out below. See
// docs/providers/IMPLEMENTATION_BACKLOG.md for what each needs to ship.
//
// Hidden as of 2026-05-20:
//   - X / Twitter   (no backend OAuth)
//   - TikTok        (no backend OAuth)
//   - Reddit        (no backend OAuth)
//   - Google        REMOVED entirely (not commented) — already a primary
//                   digital-account signal on Page 2; listing it here too was
//                   duplicate noise. Deliberate removal, not coming-soon.
//
// Live providers on this page:
//   - LinkedIn  → backend writes digital.social_account at confidence 1.00
//   - Facebook  → backend writes digital.social_account (platform #424). A real
//                 server-side OAuth provider since 2026-08-26; it previously
//                 logged in through the FB JS SDK and submitted a handle for
//                 operator review, which wrote no workflow step and produced no
//                 evidence. Gated on availability — see `gatedOnAvailability`.
//   - GitHub    → a real server-side sign-in that proves control of the account
//                 (never an email: its public address is usually unset and
//                 nothing marks it verified). Gated on availability, and hidden
//                 on a REQUIRED definition step that does not name it — see
//                 `hideWhenRequiredStepCannotUseIt`.
//   - Amazon    → backend writes digital.email_history at confidence 0.85
//                 (UI-grouped here for "supporting connections" UX, but the
//                  signal stacks with Google/Microsoft, not LinkedIn — see
//                  docs/SCORING.md §5 for the rationale)
const socialSources: SourceOption[] = [
  { id: "linkedin", name: "LinkedIn", desc: "Name and email alignment", category: "social", tenure: "6 years", corroboration: "Professional timeline partially aligned", strength: "moderate", recommended: true, tag: "High value", liveOAuth: "linkedin" },
  { id: "facebook", name: "Facebook", desc: "Name and email alignment", category: "social", tenure: "9 years", corroboration: "Social presence supports identity consistency", strength: "limited", tag: "Optional", liveOAuth: "facebook", gatedOnAvailability: true },
  { id: "amazon", name: "Amazon", desc: "Name and email alignment", category: "social", tenure: "8 years", corroboration: "Long account tenure supports continuity", strength: "moderate", liveOAuth: "amazon" },
  { id: "github", name: "GitHub", desc: "Public profile details, such as your username and when you joined", category: "social", strength: "moderate", liveOAuth: "github", gatedOnAvailability: true, hideWhenRequiredStepCannotUseIt: true },
  // { id: "twitter", name: "X / Twitter", desc: "Profile consistency signals", category: "social", tenure: "4 years", corroboration: "Limited profile consistency signal", strength: "limited", tag: "Optional" },
  // { id: "tiktok", name: "TikTok", desc: "Account tenure & activity signals", category: "social", tenure: "3 years", corroboration: "Account activity supports identity presence", strength: "limited", tag: "Optional" },
  // { id: "reddit", name: "Reddit", desc: "Account age & community presence", category: "social", tenure: "5 years", corroboration: "Long-standing account supports continuity", strength: "limited", tag: "Optional", comingSoon: true },
];

/**
 * A GitHub tile that exists ONLY in demo.
 *
 * The LIVE GitHub tile is in `socialSources` (a real sign-in, shown only when
 * the session advertises it). This one is the preview tile: it deliberately
 * carries no `liveOAuth`, and the demo list drops the live tile before adding
 * it so the two never both render.
 *
 * ⚠️ That omission is load-bearing, and this tile must never be added to
 * `socialSources`:
 *
 *  - It once also had to avoid `liveProvider`, the manual-submit path. That
 *    field is gone: platform #442 retired the path because nothing could ever
 *    review a submission. GitHub's real route is OAuth (platform #349 → #353).
 *  - On the LIVE flow, a tile with neither field falls through to the demo
 *    branch of `handleSourceClick` and "connects" in local state with no
 *    attestation and no signal — which is precisely the Facebook bug of
 *    2026-08-26: an account check that looked done, wrote nothing, and
 *    vanished on refresh. Hence `!isLive` at the call site, not a flag here.
 *
 * It also satisfies nothing on either lane: `ACCOUNT_ALTS` is LinkedIn /
 * Amazon / Facebook. That is correct rather than a gap — connecting it should
 * not advance a required step the real flow would not advance.
 */
const DEMO_ONLY_GITHUB: SourceOption = {
  id: "github",
  name: "GitHub",
  desc: "Developer identity & contribution history",
  category: "social",
  tenure: "7 years",
  corroboration: "Contribution history supports professional continuity",
  strength: "moderate",
  tag: "Preview",
};

// Other corroboration sources (service accounts, payroll, work email, edu
// email, utility, etc.). Most hidden as of 2026-05-21 — Uber is the first
// active entry and renders the "Other Corroboration Sources" subsection on
// Page 3. See IMPLEMENTATION_BACKLOG.md.
//
// Uber writes digital.email_history on the backend (see docs/SCORING.md §5
// in the digavouch-platform repo for the typing rationale). The implicit
// mobile-verified signal Uber returns is captured on the signal record and
// surfaced as "Uber account verified (phone verified)" on /applicant/:id.
const otherSources: SourceOption[] = [
  { id: "uber", name: "Uber", desc: "Name and email alignment", category: "other", tenure: "5 years", corroboration: "Long-standing service account with payment + phone backing", strength: "moderate", tag: "Recommended", liveOAuth: "uber" },
  // { id: "payroll", name: "Payroll provider", desc: "Income continuity verification", category: "other", tenure: "2 years", corroboration: "Income continuity strengthened", strength: "strong", recommended: true, tag: "High value", comingSoon: true },
  // { id: "work-email", name: "Work email / employer account", desc: "Employment domain alignment", category: "other", tenure: "3 years", corroboration: "Employer email domain aligned with stated role", strength: "moderate", recommended: true, comingSoon: true },
  // { id: "edu-email", name: "Education email / alumni account", desc: "Educational history alignment", category: "other", tenure: "6 years", corroboration: "Education history supports stated background", strength: "moderate", comingSoon: true },
  // { id: "utility", name: "Utility / housing account", desc: "Address continuity signal", category: "other", tenure: "2 years", corroboration: "Address history supports stated residence", strength: "moderate", tag: "Optional", comingSoon: true },
  // { id: "verizon", name: "Verizon", desc: "Phone account tenure & identity verification", category: "other", tenure: "4 years", corroboration: "Phone account tenure supports identity continuity", strength: "moderate", recommended: true, comingSoon: true },
];

const strengthColor = {
  strong: "bg-trust-green-muted text-trust-green",
  moderate: "bg-trust-blue-muted text-trust-blue",
  limited: "bg-trust-amber-muted text-trust-amber-foreground",
};

const MAX_INVITATIONS = 3;
const RESEND_COOLDOWN_MS = 30 * 60 * 1000;

function PrivacyCallout() {
  return (
    <div className="flow-privacy-callout flex items-start gap-2.5 rounded-xl bg-[#F5F3FF] border border-[#DDD6FE] px-4 py-3">
      <Shield className="w-4 h-4 mt-0.5 text-[#7C3AED] shrink-0" />
      <p className="text-sm leading-snug text-[#4C1D95]">
        <span className="font-semibold">Private by design.</span>{" "}
        <span className="text-[#5B21B6]">No account access shared. Messages and content stay private.</span>
      </p>
    </div>
  );
}


function relativeExpiry(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "Expired";
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days >= 1) return `Expires in ${days} day${days === 1 ? "" : "s"}`;
  const hours = Math.max(1, Math.floor(ms / (60 * 60 * 1000)));
  return `Expires in ${hours}h`;
}

export function VoucherInvitationRow({
  inv,
  onResend,
  onRevoke,
  busyId,
  isLive,
}: {
  inv: VoucherInvitation;
  onResend: (id: string) => void;
  onRevoke: (id: string) => void;
  busyId: string | null;
  /**
   * Resend renders only on the LIVE flow: it mails the voucher again, and in
   * demo there is no invitation to resend. It is the one recovery path for a
   * voucher who never got the email.
   *
   * There is deliberately no "Copy link": the link it
   * copied is a working voucher link, so it let the applicant open it and
   * vouch for themselves. Do not bring it back; Resend covers the recovery.
   */
  isLive: boolean;
}) {
  // Map backend statuses → two display states.
  // "complete" (the voucher responded) | "invited" (pending) — expired/revoked
  // surface as muted "invited".
  //
  // `"complete"` is what the applicant endpoint actually sends; `"accepted"` is
  // the legacy raw status and is never emitted (see `VoucherInvitationStatus`).
  // Testing only for `"accepted"` here is why a confirmed vouch rendered as
  // amber "Awaiting response" — telling the applicant their finished vouch was
  // still outstanding (prod, 2026-08-06).
  const isComplete = inv.status === "complete" || inv.status === "accepted";
  const isPending = inv.status === "pending";
  const cooldownLeft =
    inv.last_resent_at
      ? RESEND_COOLDOWN_MS - (Date.now() - new Date(inv.last_resent_at).getTime())
      : 0;
  const resendDisabled = !isPending || cooldownLeft > 0 || busyId === inv.invitation_id;

  const confirmRemove = () => {
    if (window.confirm("Are you sure you want to remove this voucher? This will revoke their invite and update your verification progress.")) {
      onRevoke(inv.invitation_id);
    }
  };

  const containerCls = isComplete
    ? "border-[1.5px] border-[#16A34A] bg-[#F0FDF4] rounded-lg p-3 space-y-2"
    : "border-[1.5px] border-[#E2E8F0] bg-card rounded-lg p-3 space-y-2";

  return (
    <div className={containerCls}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-foreground truncate">{inv.name}</p>
            {isComplete ? (
              /*
                👤 Copy decision for Juan + Patrick. Deliberately "Responded",
                not "Vouched": the backend collapses BOTH a confirmed vouch and
                a refusal into `complete` (it does not want the applicant to
                know which), so a green "Vouched ✓" would be an overclaim on a
                voucher who declined to endorse. Green is kept — the step IS
                done — but the word no longer asserts an outcome the applicant
                has not actually been told. Revert only alongside a decision
                about what a declined vouch should look like here.
              */
              <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#15803D]">
                Responded
              </span>
            ) : (
              <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">
                Invited
              </span>
            )}
          </div>
          {/* The relationship arrives lower case from the API and is the
              only part of this line meant to be capitalised. The class used
              to sit on the whole <p>, which handed the applicant back
              "Juan+Voucher@Digavouch.Com": text-transform capitalises the
              first letter of every word and a browser breaks words on +, @
              and . as well as spaces. This row is the applicant's only
              confirmation of who they invited, so the address has to read
              exactly as they typed it. */}
          <p className="text-xs text-muted-foreground">
            <span className="capitalize">{inv.relationship}</span> · {inv.email} ·{" "}
            {isComplete ? "Response received ✓" : "Awaiting response"}
          </p>
          {isPending && <p className="text-[11px] text-muted-foreground mt-0.5">{relativeExpiry(inv.expires_at)}</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {isLive && isPending && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs border-[#FDE68A] bg-[#FFFBEB] text-[#92400E] hover:bg-[#FEF3C7] hover:text-[#92400E]"
            onClick={() => onResend(inv.invitation_id)}
            disabled={resendDisabled}
            title={cooldownLeft > 0 ? `Try again in ${Math.ceil(cooldownLeft / 60000)} min` : undefined}
          >
            {busyId === inv.invitation_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            Resend link
          </Button>
        )}
        {/*
          Pending only. The backend revokes `WHERE status = 'pending'`, so
          removing an already-responded invitation 404s — and `handleRevoke`
          treats a 404 as success, drops the row and toasts "Invitation
          removed", after which the 12s poll fetches it straight back. Offering
          the action at all promises something the applicant cannot have: a
          completed vouch is not withdrawable from this screen.
        */}
        {isPending && (
          <button
            type="button"
            onClick={confirmRemove}
            disabled={busyId === inv.invitation_id}
            className="inline-flex items-center gap-1 text-xs text-[#94A3B8] hover:text-[#EF4444] transition-colors disabled:opacity-50 ml-auto"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
      </div>
    </div>
  );
}
/**
 * Which demo lane the viewer was last on, so a switch between lanes can start
 * the new one clean. sessionStorage rather than a ref: leaving for the demo hub
 * unmounts this component, and the marker has to outlive that.
 */
const DEMO_LANE_KEY = "dv_demo_lane";

/* ============================================================================
 * Demo-mode placeholder for EmailCodePanel — visually mirrors the locked-email
 * card in Scenario B of Step 1, but is non-interactive (no backend token).
 * ========================================================================== */
function DemoLockedEmailStub({ email }: { email: string }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background text-sm">
        <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <span className="font-mono text-foreground truncate">{email}</span>
      </div>
      <Button disabled variant="outline" className="w-full">
        <Mail className="w-4 h-4 mr-2" />
        Send verification code to {email}
      </Button>
      <p className="text-[11px] text-muted-foreground italic text-center">
        Preview only — verification codes are sent in the live applicant flow.
      </p>
    </div>
  );
}


export type VoucherInviteFormHandle = { openForm: () => void };

const VoucherInviteForm = forwardRef<VoucherInviteFormHandle, { token?: string; maxInvitations?: number; onInvitationsChange?: (invitations: VoucherInvitation[]) => void; recruiting?: boolean; applicantEmail?: string; articlePresentation?: boolean }>(function VoucherInviteForm({ token, maxInvitations = MAX_INVITATIONS, onInvitationsChange, recruiting = false, applicantEmail, articlePresentation = false }, ref) {
  const isLive = Boolean(token);
  const storageKey = `dv_voucher_invite_${token ?? "demo"}`;
  const queryClient = useQueryClient();
  const invitationsQueryKey = useMemo(
    () => ["applicant", "voucher-invitations", token] as const,
    [token],
  );
  const notifyChange = (next: VoucherInvitation[]) => { onInvitationsChange?.(next); };

  // Live mode: source-of-truth is the shared React Query cache that is
  // polled every 12s by useVoucherInvitations. Keeping a separate local
  // copy in this form previously caused invites to "disappear" from the
  // UI after a remount or polling refresh while the backend still had
  // them on file — producing a confusing "Already invited" error when
  // the applicant tried to re-add the same email.
  const { invitations: liveInvitations, loading: liveLoading, refetch: refetchLive } =
    useVoucherInvitations(token);

  const [demoInvitations, setDemoInvitations] = useState<VoucherInvitation[]>([]);
  const invitations = isLive ? liveInvitations : demoInvitations;
  const loading = isLive ? liveLoading : false;

  const setLiveCache = (updater: (prev: VoucherInvitation[]) => VoucherInvitation[]) => {
    queryClient.setQueryData<VoucherInvitation[]>(invitationsQueryKey, (prev) => updater(prev ?? []));
  };
  const setInvitations = (next: VoucherInvitation[] | ((prev: VoucherInvitation[]) => VoucherInvitation[])) => {
    if (isLive) {
      setLiveCache((prev) => (typeof next === "function" ? (next as (p: VoucherInvitation[]) => VoucherInvitation[])(prev) : next));
    } else {
      setDemoInvitations((prev) => (typeof next === "function" ? (next as (p: VoucherInvitation[]) => VoucherInvitation[])(prev) : next));
    }
  };

  const [showForm, setShowForm] = useState(!isLive);
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  /**
   * The free-text detail shown when "Other" is picked.
   *
   * ⚠️ It is NOT sent as `relationship`. `ALL_RELATIONSHIP_OPTIONS` is the only
   * set the invite endpoint accepts — it 400s anything outside
   * `VALID_RELATIONSHIPS` in the platform repo — so the wire value stays
   * "other" and this rides along as the display label. On the live flow that
   * means the detail is not persisted: the next poll re-renders the row from
   * the server's "other". Persisting it needs a backend field.
   */
  const [otherRelationship, setOtherRelationship] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Shared predicate rather than a local status list: this filter and the
  // submission counters must agree, and they didn't. `"complete"` (a voucher
  // who responded) was missing here, so a confirmed vouch vanished from the
  // list while the backend still held the row — and re-inviting the same
  // address then 409'd as a duplicate (prod, 2026-08-06).
  const activeInvitations = useMemo(
    () => invitations.filter((inv) => isOpenVouchInvite(inv.status)),
    [invitations],
  );

  useImperativeHandle(ref, () => ({
    openForm: () => setShowForm(true),
  }), []);

  // Demo: rehydrate from sessionStorage once.
  useEffect(() => {
    if (token) return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) setDemoInvitations(JSON.parse(raw));
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Show form by default when there are no invitations
  useEffect(() => {
    if (!loading && activeInvitations.length === 0) setShowForm(true);
  }, [loading, activeInvitations.length]);

  const persistDemo = (next: VoucherInvitation[]) => {
    if (token) return;
    try { sessionStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
  };

  /**
   * ADR-015 R1 — the applicant's own address cannot be their voucher.
   *
   * Shown while typing rather than on submit: the server returns 422
   * `SELF_INVITE` either way, but a failed round trip is a worse place to learn
   * it. Silent until the address is actually a self-match, so a half-typed
   * address never accuses anyone.
   */
  const isSelfAddress = useMemo(
    () => isSelfInvite(applicantEmail, email),
    [applicantEmail, email],
  );

  const canSend =
    name.trim().length > 1 &&
    relationship &&
    !isSelfAddress &&
    // "Other" without the detail is less information than picking nothing —
    // the row would read "Other" and the voucher would be told they are the
    // applicant's "other".
    (relationship !== "other" || otherRelationship.trim().length > 0) &&
    email.includes("@") &&
    activeInvitations.length < maxInvitations;

  const resetForm = () => { setName(""); setRelationship(""); setOtherRelationship(""); setEmail(""); };

  const handleSend = async () => {
    setBusy(true);
    // What the ROW shows. The wire value stays `relationship` — see the note on
    // `otherRelationship` — so these two deliberately differ for "other", and
    // on the live flow the next poll replaces the label with the server's
    // "other" until a backend field exists to hold it.
    const displayRelationship =
      relationship === "other" && otherRelationship.trim()
        ? otherRelationship.trim()
        : relationship;
    try {
      if (token) {
        const res = await applicantApi.inviteVoucher(token, { name, email, relationship });
        const newInv: VoucherInvitation = {
          invitation_id: res.invitation_id,
          name, email, relationship: displayRelationship,
          status: "pending",
          sent_at: new Date().toISOString(),
          expires_at: res.expires_at,
          voucher_link: res.voucher_link,
        };
        setInvitations((prev) => {
          const next = [...prev, newInv];
          notifyChange(next);
          return next;
        });
        toast({ title: "Invitation sent", description: `${name} will get a link to vouch for you.` });
      } else {
        // Demo simulation
        const newInv: VoucherInvitation = {
          invitation_id: `demo-${Date.now()}`,
          name, email, relationship: displayRelationship,
          status: "pending",
          sent_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          voucher_link: `${window.location.origin}/voucher-flow?token=demo-${Date.now()}`,
        };
        const next = [...invitations, newInv];
        setInvitations(next);
        persistDemo(next);
        notifyChange(next);
        toast({ title: "Invitation sent", description: `${name} will get a link to vouch for you.` });
      }
      resetForm();
      setShowForm(false);
      if (isLive) refetchLive();
    } catch (e) {
      const err = e as ApiError;
      // Dispatch lives in voucherInviteErrorToast so the SELF_INVITE and
      // max-invitations cases, which share status 422, cannot be reordered
      // into each other. MAX_INVITATIONS is the server's cap, deliberately not
      // the `maxInvitations` prop (that one is requiredVouches).
      const { title, description, refetch } = voucherInviteErrorToast(err, MAX_INVITATIONS);
      toast({ title, description, variant: "destructive" });
      if (refetch && isLive) refetchLive();
    } finally { setBusy(false); }
  };

  const handleResend = async (id: string) => {
    setBusyId(id);
    try {
      if (token) {
        const res = await applicantApi.resendVoucherInvitation(token, id);
        setInvitations((list) => list.map((i) => i.invitation_id === id
          ? { ...i, last_resent_at: res.last_resent_at, voucher_link: res.voucher_link ?? i.voucher_link }
          : i));
      } else {
        const stamped = new Date().toISOString();
        const next = invitations.map((i) => i.invitation_id === id ? { ...i, last_resent_at: stamped } : i);
        setInvitations(next); persistDemo(next);
      }
      toast({ title: "Invitation re-sent" });
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 429) {
        toast({ title: "Slow down", description: "You can resend once every 30 minutes.", variant: "destructive" });
      } else {
        toast({ title: "Could not resend", description: err.message, variant: "destructive" });
      }
    } finally { setBusyId(null); }
  };

  const handleRevoke = async (id: string) => {
    // Confirmation handled in VoucherInvitationRow before calling onRevoke.
    setBusyId(id);
    try {
      if (token) await applicantApi.revokeVoucherInvitation(token, id);
      const next = invitations.filter((i) => i.invitation_id !== id);
      setInvitations(next);
      if (!token) persistDemo(next);
      notifyChange(next);
      if (isLive) refetchLive();
      toast({ title: "Invitation revoked" });
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 404 || err.status === 410) {
        const next = invitations.filter((i) => i.invitation_id !== id);
        setInvitations(next);
        if (!token) persistDemo(next);
        notifyChange(next);
        toast({ title: "Invitation removed" });
      } else {
        toast({ title: "Could not revoke", description: err.message, variant: "destructive" });
      }
    } finally { setBusyId(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading invitations…
      </div>
    );
  }

  const atCap = activeInvitations.length >= maxInvitations;

  return (
    <div className={articlePresentation ? "article-invite-form-content space-y-3" : "space-y-3"}>
      {activeInvitations.map((inv) => (
        <VoucherInvitationRow
          key={inv.invitation_id}
          inv={inv}
          onResend={handleResend}
          onRevoke={handleRevoke}
          busyId={busyId}
          isLive={isLive}
        />
      ))}

      {!showForm && !atCap && activeInvitations.length > 0 && (
        <Button variant="ghost" size="sm" className={articlePresentation ? "article-invite-add w-full text-xs" : "w-full text-xs"} onClick={() => setShowForm(true)}>
          <Plus className="w-3 h-3 mr-1" /> Invite another person
        </Button>
      )}

      {atCap && !articlePresentation && (
        <p className="text-xs text-muted-foreground italic text-center">
          You've added {maxInvitations} {maxInvitations === 1 ? "person" : "people"}. That completes this step.
        </p>
      )}

      {showForm && !atCap && (
        <div className={articlePresentation ? "article-invite-fields space-y-3" : "voucher-invite-form-card border border-border rounded-lg p-3 space-y-3"}>
          {activeInvitations.length > 0 && (
            <p className="text-sm font-medium text-foreground">
              {articlePresentation ? "Invite another person" : "Invite another voucher"}
            </p>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Full name</label>
            <Input placeholder="e.g. Sarah Chen" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Relationship</label>
            <Select
              value={relationship}
              onValueChange={(next) => {
                setRelationship(next);
                // Clear on the way OUT of "other", so a detail typed earlier
                // cannot ride along under a relationship it does not describe.
                if (next !== "other") setOtherRelationship("");
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select relationship type" />
              </SelectTrigger>
              <SelectContent>
                {relationshipOptionsFor(recruiting).map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {relationship === "other" && (
              <Input
                className="mt-2"
                placeholder="How do you know them? e.g. Mentor"
                value={otherRelationship}
                onChange={(e) => setOtherRelationship(e.target.value)}
                maxLength={40}
              />
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Email</label>
            <Input
              type="email"
              placeholder="sarah@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={isSelfAddress || undefined}
              aria-describedby={isSelfAddress ? "voucher-email-self" : undefined}
            />
            {isSelfAddress && (
              <p id="voucher-email-self" className="text-xs text-[#EF4444] mt-1.5">
                {SELF_INVITE_MESSAGE}
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
            <Button
              className={articlePresentation ? "article-invite-send flex-1" : "flex-1 bg-[#7C3AED] hover:bg-[#6D28D9] text-white"}
              onClick={handleSend}
              disabled={!canSend || busy}
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send invitation
            </Button>
            {activeInvitations.length > 0 && (
              <Button
                variant="outline"
                className="w-full sm:w-32 border-[#E2E8F0] text-[#64748B] hover:text-foreground"
                onClick={() => { resetForm(); setShowForm(false); }}
                disabled={busy}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

const categories = [
  { key: "social", label: "Social & Professional Accounts", icon: Globe, sources: socialSources },
  { key: "other", label: "Other Corroboration Sources", icon: Link2, sources: otherSources },
] as const;

// EmailCodePanel (live-mode email-OTP UI) extracted to
// src/components/EmailCodePanel.tsx on 2026-05-20 so the voucher flow can
// reuse it. No behavior change — verbatim move plus the import below.


/* ============================================================================
 * Live-mode phone step-up
 * ========================================================================== */
function PhoneStepUp({ token, onComplete }: { token: string; onComplete: () => void }) {
  const [phone, setPhone] = useState("+1 ");
  const [code, setCode] = useState("");
  const [phoneId, setPhoneId] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      // The backend keys the confirm off the row id it mints here, not off
      // the number.
      const res = await applicantApi.sendPhoneCode(token, phone.trim());
      setPhoneId(res.phone_id);
      setSent(true);
      toast({ title: "Code sent", description: `We texted a code to ${phone}.` });
    } catch (e) {
      toast({ title: "Could not send code", description: (e as ApiError).message, variant: "destructive" });
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!phoneId) return;
    setBusy(true);
    let succeeded = false;
    try {
      // A wrong code is `200 {verified:false}`, not an exception — same
      // contract as the email OTP (see applicantApi.confirmPhoneCode).
      const result = await applicantApi.confirmPhoneCode(token, phoneId, code);
      if (result?.verified) {
        succeeded = true;
      } else {
        // Keep the mistyped code visible — same rule as EmailCodePanel: an
        // async continuation must not clobber input the user may have retyped
        // while the request was in flight.
        toast({
          title: "That code didn't match",
          description: "Check the code and try again.",
          variant: "destructive",
        });
      }
    } catch (e) {
      toast({ title: "Couldn't verify that code", description: (e as ApiError).message, variant: "destructive" });
    } finally { setBusy(false); }

    // Outside the try: a failing onComplete is not a verification failure.
    if (!succeeded) return;
    toast({ title: "Phone verified" });
    onComplete();
  };


  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-trust-amber-foreground bg-trust-amber-muted rounded-lg p-3 text-xs">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>Additional verification needed. Please confirm your mobile number.</span>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground block mb-1">Mobile number</label>
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} disabled={sent} />
      </div>
      {sent && (
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">6-digit code</label>
          <Input
            value={code} maxLength={6}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="text-center tracking-[0.5em] font-mono"
          />
        </div>
      )}
      {!sent ? (
        <Button className="w-full" onClick={send} disabled={busy || phone.trim().length < 8}>
          {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
          <Phone className="w-4 h-4 mr-1" /> Send code
        </Button>
      ) : (
        <Button className="w-full" onClick={confirm} disabled={busy || code.length < 6}>
          {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Verify
        </Button>
      )}
    </div>
  );
}

/* ============================================================================
 * Main ApplicantFlow
 * ========================================================================== */
export default function ApplicantFlow({ presentationFlow = null }: { presentationFlow?: DemoFlow | null } = {}) {
  const { state, setMode, setCurrentStep, completeStepUp, connectSource, disconnectSource, resetApplicantFlow, simulateLeaveAndReturn } = useFlow();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant } = useTenant();
  const token = params.token;
  const isLive = Boolean(token);
  useEffect(() => { initClarity(); }, []);

  // Standalone redesigned Step 1 preview — not wired to anything.
  // Reachable at /demo/applicant-flow?step=1new
  // NOTE: conditional render lives at the bottom of the component so that
  // all hooks below fire unconditionally (React Rules of Hooks).
  const showStep1NewPreview = !token && searchParams.get("step") === "1new";

  // Demo lane selector — `/demo/applicant-flow?flow=tech_role|high_volume`.
  //
  // Demo-only by construction (`!token`), so a live applicant's URL can never
  // name a lane; live sessions get their definition from the server as before.
  // With no `?flow=` this is null and the demo renders LEGACY_PLAN — the
  // leasing journey, byte-for-byte as it was. That is the e2e J1 gate's
  // subject, so it must stay the default.
  const implementationMode = !token && presentationFlow !== null;
  // The walkthrough's extras (the GitHub preview tile, its tile order, the ID
  // step's label) belong to the tokenless fixture only. A live run under
  // /feather/ is the real session and must render what /verify/:token renders:
  // the fixture's tile order would hide Google and Microsoft, which the live
  // account step accepts.
  const featherPresentation = !isLive && (pathname === "/feather" || pathname.startsWith("/feather/"));
  const demoFlow = !token
    ? presentationFlow ?? demoFlowFromParam(searchParams.get("flow"))
    : null;



  // Sync mode + token into context
  useEffect(() => {
    setMode("live", token ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Voucher invitations + submission eligibility — fetched at the page
  // level so the final-step Submit gate and the per-vouch row copy share
  // a single source of truth. `useVoucherInvitations` is React-Query
  // backed, so the nested `VoucherInviteForm` dedupes against this fetch.
  const { invitations: allInvitations } = useVoucherInvitations(token);
  const queryClient = useQueryClient();
  // Bumped whenever VoucherInviteForm mutates — forces demoInvitations
  // useMemo to re-read sessionStorage immediately (no waiting for a step
  // change), and invalidates the live React-Query cache so allInvitations
  // refetches without waiting for the 12s poll.
  const [invitationsTick, setInvitationsTick] = useState(0);
  const handleInvitationsChange = (next?: VoucherInvitation[]) => {
    setInvitationsTick((t) => t + 1);
    if (token) {
      // Optimistically update the React-Query cache so Step 5 sees the
      // new list immediately — avoids a race after a revoke + re-invite
      // where the refetch hasn't returned yet.
      if (next) {
        queryClient.setQueryData(
          ["applicant", "voucher-invitations", token],
          next,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["applicant", "voucher-invitations", token] });
    }
  };
  // `useSessionPolling` replaces the manual "Refresh status" button —
  // 12s interval while the tab is visible, stops once the application is
  // ready for operator review.
  const { session, loading, error, refresh } = useSessionPolling(token, {
    stop: false, // updated below once `submission` is computed
  });

  // Demo-mode parity: build a synthetic SessionStatus + voucher invitation
  // list from FlowContext + sessionStorage so the demo applicant journey
  // exercises the same Step-1 email-routing and Step-5 submission copy as
  // the live flow. See answers to questions on 2026-06:
  //   - Demo email is forced to the OTP-led layout (juan.alvarez@protonmail.com).
  //   - Dynamic submission copy is rendered, but Submit stays always-enabled
  //     in demo so reviewers can finish the walkthrough without simulating
  //     vouch completions.
  const DEMO_APPLICANT_EMAIL = "juan.alvarez@protonmail.com";
  const demoInvitations = useMemo<VoucherInvitation[]>(() => {
    if (token) return [];
    try {
      const raw = sessionStorage.getItem("dv_voucher_invite_demo");
      return raw ? (JSON.parse(raw) as VoucherInvitation[]) : [];
    } catch {
      return [];
    }
    // Re-read whenever the user re-enters the page, the step changes,
    // or a child VoucherInviteForm mutates (invitationsTick).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, state.currentStep, invitationsTick]);
  // Demo-only stand-in for the Plaid IDV result. The real card needs a session
  // token to mint a link token, so on `?flow=tech_role` — where the definition
  // makes `plaid_idv` a required second screen — there is nothing to render
  // without this. Local state, not sessionStorage: a demo walkthrough should
  // start clean on reload rather than resuming half-verified.
  const [demoIdvVerified, setDemoIdvVerified] = useState(false);
  /**
   * Leave the Trust Checkpoint.
   *
   * The confirmation only renders on step 0, so the footer line has to return
   * there before opening it — otherwise the click set the stage and nothing
   * appeared, which is what "the link doesn't work" was.
   */
  const exitCheckpoint = () => {
    if (isLive) setLiveViewStep(APPLICANT_STEP.CONSENT);
    else setCurrentStep(APPLICANT_STEP.CONSENT);
    setDeclineStage("confirm");
  };

  const demoSession = useMemo<SessionStatus | null>(() => {
    if (token) return null;
    return {
      session_id: "demo",
      // A recruiting lane reads wrong under a leasing org name, and the flow's
      // copy is already recruiting-flavoured wherever `definitionPlan` is set.
      org_name: demoFlow?.orgName ?? DEMO_LEASING_ORG,
      // The REAL field the vertical copy reads. Without it a recruiting lane
      // renders leasing wording ("property team"), which is the drift the
      // vertical-aware work exists to kill — not a sixth proxy for it.
      org_industry: demoFlow?.industry ?? "residential_leasing",
      applicant_name: "Juan Alvarez",
      applicant_email: DEMO_APPLICANT_EMAIL,
      status: "in_progress",
      current_step: Math.max(1, state.currentStep + 1),
      percent_complete: 0,
      // Derived from the lane's own satisfiers — see `demoAvailableOAuth`.
      // The leasing demo keeps the empty list it always had, so its tiles are
      // unchanged; only a lane advertises anything.
      available_oauth_providers: demoAvailableOAuth(demoFlow),
      signals_summary: [],
      connected_providers: state.connectedSources.map((s) => ({
        provider: s.id,
        status: "connected" as const,
        masked_identifier: s.name,
        // What the server would say (platform#712). The demo's Microsoft tile
        // stands in for a personal Outlook sign-in, which proves the address.
        email_proven: DEMO_EMAIL_PROVING_TILES.has(s.id),
      })),
      pending_vouchers_count: demoInvitations.filter((i) => isOpenVouchInvite(i.status))
        .length,
      // Null on the leasing demo, which is what keeps it on LEGACY_PLAN:
      // `isDefinitionDriven` treats null as "no key", same guard as the server.
      workflow_key: demoFlow?.key ?? null,
      workflow_steps: demoFlow?.steps ?? null,
    };
  }, [token, state.currentStep, state.connectedSources, demoInvitations, demoFlow]);
  const effectiveSession = isLive ? session : demoSession;

  // ---- Flow plan (ADR-014) ------------------------------------------------
  //
  // `definitionPlan` is non-null for a session whose workflow_key is set and is
  // not 'standard' — a live session carrying one, or the demo with `?flow=`
  // naming an IDW lane. Everything else — the demo with no `?flow=`, every
  // leasing session, a detail fetch that failed — gets LEGACY_PLAN, which is
  // the hardcoded flow exactly as it was.
  //
  // Everything downstream reads `plan` / `planIndices` instead of
  // `visibleStepIndices`, `totalVisibleSteps` and `nextVisibleStep`. Those
  // comparisons used to be over raw indices and assumed plan order and index
  // order were the same thing; `rankOfIndex` preserves that ordering exactly
  // for the legacy plan while letting a definition put IDV (index 6) second.
  // Reads `effectiveSession`, not `session`: for a live session the two are the
  // same object, so nothing on that path changes. In demo it is the synthetic
  // session, which is how `?flow=` reaches the plan at all.
  const definitionPlan = useMemo(
    () => buildDefinitionPlan(effectiveSession?.workflow_key, effectiveSession?.workflow_steps),
    [effectiveSession?.workflow_key, effectiveSession?.workflow_steps],
  );
  const plan: FlowPlanEntry[] = definitionPlan ?? LEGACY_PLAN;
  const planIndices = useMemo(() => plan.map((entry) => entry.index), [plan]);
  // The server's record of what this applicant has actually done — the input to
  // both the frontier and the stepper's green/amber decision on a definition
  // plan, so the two cannot disagree.
  //
  // `applicant_idv_verified` is folded in because the two can lag each other:
  // the PortFi attestation is what `/status` reports, while `plaid_idv` reaches
  // `steps_completed` from the client, the STATUS_UPDATED webhook, or the
  // reconciliation sweep — whichever gets there first. Treating a verified IDV
  // as the step being done is what the old stepHasEvidence[6] already did; it
  // now also stops a verified applicant's frontier from stalling on IDV.
  const stepsCompleted = useMemo(() => {
    const done = new Set(session?.steps_completed ?? []);
    if (session && isIdvSatisfied(session)) done.add("plaid_idv");
    return done;
  }, [session]);
  const planEntryFor = useCallback(
    (idx: number): FlowPlanEntry =>
      // A step index outside the plan can only be the hidden work step (3) on
      // the legacy path, which the backend's step counter can still produce.
      // Falling back to the raw `steps` entry is what rendered there before.
      plan.find((entry) => entry.index === idx) ?? {
        index: idx,
        title: steps[idx].title,
        desc: steps[idx].desc,
        icon: steps[idx].icon,
      },
    [plan],
  );
  // ---- The org's vertical (slice D) ---------------------------------------
  //
  // Recruiting rather than leasing framing. This used to be
  // `definitionPlan !== null` — proxy 3 of the five in the vertical-aware case
  // model. That asks "does this session run a workflow definition?", which is a
  // different question that has agreed with this one by accident: every
  // definition-driven org so far happens to be a recruiter, and every recruiter
  // happens to run a definition. Giving a leasing org a bespoke flow breaks the
  // first half; letting a recruiter run the default flow breaks the second.
  //
  // `org_industry` is the org's real vertical, off `GET /sessions/:token/status`
  // — the only route a magic-link visitor has, and the same field the voucher
  // flow reads. Absent (demo mode, an older server) resolves to leasing, which
  // is what every one of these surfaces rendered before the field existed.
  //
  // ⚠️ NOT `useOperatorVertical()`. A visitor here has no operator membership;
  // calling it outside a ProtectedRoute subtree issues a request that returns
  // nothing useful, and a signed-in operator opening an applicant's link would
  // read their OWN org's vertical onto someone else's page.
  // `effectiveSession`, not `session`: identical for a live session, and it is
  // what lets the demo lanes render recruiting copy off their own org_industry.
  const vertical = verticalFrom(effectiveSession?.org_industry);
  const copy = copyForIndustry(effectiveSession?.org_industry);
  const isRecruiting = isRecruitingVertical(vertical);
  // Page-1 "Trust Checkpoint" framing, or null on a vertical without it — in
  // which case every block below falls back to the copy it rendered before.
  const checkpoint = copy.checkpointIntro;
  const checkpointOrg = effectiveSession?.org_name ?? `the ${copy.reviewingTeam}`;
  // One consent sentence for every look: the decision it disclaims comes
  // from the copy deck, never a per-partner string.
  const consentSentence = applicantConsentSentence(checkpointOrg, copy);
  const withOrg = (t: string) => t.replace("{org}", checkpointOrg);
  // The partner look (lib/flowPresentation.ts): a partner tenant, or a live
  // recruiting session. A demo lane keeps its own look, so only a live
  // session's industry counts here.
  const liveIndustry = isLive ? session?.org_industry : undefined;
  const articlePresentation = implementationMode || usesPartnerLook(tenant?.branding?.partner_key, liveIndustry);
  // Page 1's exit on the new look: a partner tenant (Article, Feather, and their
  // review routes) keeps its "Continue standard review" link. Every other org on
  // the new look shows the copy deck's optional line there, the one every later
  // screen shows, so the optional wording is on every screen (Juan, 2026-10-17).
  const partnerTenantLook = implementationMode || isPartnerLookKey(tenant?.branding?.partner_key);
  // Tell the shell, so the chrome around a magic-link page follows the session
  // rather than whoever is looking at it, and waits for it instead of guessing.
  usePublishSessionVertical(liveIndustry, !isLive || session !== null || error !== null);
  // Upper bound for clamping a step index that arrived from outside (an OAuth
  // return's `?step=`, or the sessionStorage stash). The legacy plan's highest
  // index is 5, so this is the old `steps.length - 1` bound unchanged — that
  // expression stopped meaning "the last step" the moment index 6 was appended.
  const maxPlanIndex = useMemo(
    () => planIndices.reduce((a, b) => (b > a ? b : a), 0),
    [planIndices],
  );

  // Provider tiles the backend can actually complete a flow for. A gated tile
  // is absent rather than disabled: showing a tile the backend would reject
  // ("Apple OAuth not configured") is a dead end the applicant can't act on.
  const visibleAccountOptions = useMemo(() => {
    const available = effectiveSession?.available_oauth_providers ?? [];
    return accountOptions.filter(
      (opt) => !opt.gatedOnAvailability || (!!opt.provider && available.includes(opt.provider)),
    );
  }, [effectiveSession?.available_oauth_providers]);
  const effectiveInvitations = isLive ? allInvitations : demoInvitations;
  const submission = getSubmissionState(effectiveSession, effectiveInvitations);
  // Which rule this session's vouch screen and Submit follow. Null is the
  // account tier, unchanged. Non-null only when the server says the session's
  // Flow states its own vouch number, in which case the number and "can
  // submit" are the server's, read rather than re-derived. Demo sessions carry
  // no such field, so the demo keeps the tier.
  const flowRule = isLive ? flowSubmissionRule(session) : null;
  // The server's vouch numbers on EVERY session, account tier included, when
  // the backend publishes them: how many invites to ask for and whether Submit
  // opens. Only the numbers: `flowRule` above still picks the Flow layout, so a
  // leasing session keeps the leasing layout. Null on an older backend and in
  // the demo, which keep the arithmetic.
  const serverRule = isLive ? serverSubmissionRule(session) : null;
  const numbersRule = serverRule ?? flowRule;
  // Submit reads the server's `can_submit`, which moves when an invite goes
  // out. The invitation list updates at once but the session only polls every
  // 12s, so ask for the new answer now rather than leave Submit disabled over a
  // gate that has already opened.
  const sentInvitesForRefresh = submission.sentVouchInvites;
  useEffect(() => {
    if (numbersRule) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentInvitesForRefresh]);

  // Handle OAuth callback toast (corner toast that auto-dismisses)
  useEffect(() => {
    const oauth = searchParams.get("oauth");
    const status = searchParams.get("status");
    const stepParam = searchParams.get("step");
    if (oauth) {
      const providerLabel = oauthProviderLabel(oauth);
      if (status === "success" && searchParams.get("reason") === "email_not_proven") {
        // platform#712: the account connected, but this sign-in did not confirm
        // the email address (for example a work account whose organization has
        // not verified its email domain). Name the next step, no dead end.
        sonnerToast.success(`${providerLabel} connected`, {
          description: "To confirm your email address, use a one-time code on the email step.",
        });
      } else if (status === "success") {
        sonnerToast.success(`${providerLabel} connected`, {
          description: "We refreshed your session.",
        });
      } else {
        sonnerToast.error(`${providerLabel} connection canceled`, {
          description: "The sign-in window was closed or canceled. You can try again or use a different method.",
        });
      }
      void refresh();
      // Restore the step the user was on before the OAuth handoff.
      //
      // The backend success-path redirect goes straight to
      // /verify/:token?oauth=X&status=success with NO ?step= param (it doesn't
      // know which step the applicant clicked from). So we fall back to
      // dv_oauth_return_step in sessionStorage, which ApplicantFlow stashes
      // immediately before window.location to the OAuth provider (see the
      // sessionStorage.setItem block in startOAuth handler). Without this
      // fallback, the user lands back on step 1 (consent) after a successful
      // OAuth, which feels like a redirect loop.
      let resolvedStepParam: string | null = stepParam;
      if (!resolvedStepParam) {
        try {
          resolvedStepParam = sessionStorage.getItem("dv_oauth_return_step");
        } catch { /* ignore */ }
      }
      // Always clear the sessionStorage entries on return — single-use.
      try {
        sessionStorage.removeItem("dv_oauth_return_token");
        sessionStorage.removeItem("dv_oauth_return_step");
      } catch { /* ignore */ }
      if (resolvedStepParam) {
        const parsed = parseInt(resolvedStepParam, 10);
        if (!Number.isNaN(parsed)) {
          const target = Math.min(Math.max(parsed - 1, 0), maxPlanIndex);
          setLiveSkippedThrough((prev) => laterInPlan(planIndices, prev, target));
          setLiveViewStep(target);
        }
      }
      // Strip the query params so refreshing the page doesn't re-fire the toast
      const params = new URLSearchParams(searchParams);
      params.delete("oauth");
      params.delete("status");
      params.delete("step");
      params.delete("reason");
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("oauth"), searchParams.get("status")]);

  // Backend is 1-indexed (1=consent, 2=digital, ...); UI stepper is 0-indexed.
  // In live mode, `furthestStep` is the highest step the applicant has reached
  // (driven by the backend). `viewStep` is the step currently being displayed
  // and is owned by the UI — applicants can navigate backward freely and
  // forward up to `furthestStep`.
  //
  // ⚠️ `session.current_step` is NOT a counter and NOT a plan position. It is
  // `computed_step` from GET /sessions/:token/status — a four-value bucket
  // (1 / 2 / 3 / 6) whose values are 1-based INDICES into the legacy six-step
  // array. The legacy path below subtracts one and reads it as exactly that,
  // which is correct and stays untouched.
  //
  // A definition plan cannot use it. `tech_role` orders IDV (index 6) second,
  // so its plan positions and the legacy indices diverge, and resolving the
  // bucket as a position sent bucket 2 to the IDV screen and bucket 3 to email
  // — meaning any OAuth connection (which moves the bucket to 3) threw the
  // applicant back to "Verify your email" with screens 4-6 unreachable, since
  // `liveSkippedThrough` resets to 0 across the OAuth page load. The bucket
  // also saturates at 3 until every REQUIRED step is satisfied, and
  // `tech_role` requires `plaid_idv`.
  //
  // So the definition path derives the frontier from `steps_completed` against
  // the definition's own `satisfied_by` — the same predicate the server gates
  // on. See `frontierIndexInPlan` for the full rationale.
  //
  // The bucket still carries one thing evidence cannot: bucket 6 IS the
  // server's `isFinished`, and the vouch/review screens are not workflow steps,
  // so nothing in `steps_completed` can name them. That single bit is read as a
  // boolean, never as a position.
  const backendStep = isLive
    ? definitionPlan
      ? frontierIndexInPlan(definitionPlan, stepsCompleted, {
          finished: (session?.current_step ?? 1) >= 6,
        })
      : Math.max(0, (session?.current_step ?? 1) - 1)
    : state.currentStep;
  // Tracks steps the applicant skipped locally (no backend evidence yet).
  // Skipped steps are reachable forward/backward in the stepper but render as
  // amber/unfilled until the user actually completes them.
  const [liveSkippedThrough, setLiveSkippedThrough] = useState<number>(0);
  // `laterInPlan` rather than `Math.max`: on a definition plan the higher index
  // is not the later step. Identical to `Math.max` for the legacy plan, whose
  // ranks are strictly increasing in index (see rankOfIndex).
  const furthestStep = isLive
    ? laterInPlan(planIndices, backendStep, liveSkippedThrough)
    : backendStep;
  const [liveViewStep, setLiveViewStep] = useState<number | null>(null);

  // Initialize the view to wherever the backend says the applicant is on first
  // load. After that, advancing is driven by the user clicking "Continue" (or
  // by the OAuth callback handler, which sets liveViewStep explicitly). We do
  // NOT auto-advance on every furthestStep bump, otherwise verifying a single
  // signal (e.g. one email) would yank the user off the current step before
  // they've had a chance to add more evidence on it.
  //
  // OAuth return resilience: also check sessionStorage for a stashed step
  // before defaulting to furthestStep. The dedicated ?oauth= callback effect
  // above handles the normal case (URL has ?oauth=X&status=success), but if
  // the provider/backend strips query params on redirect we'd otherwise land
  // the user on furthestStep, which can be LOWER than where they kicked off
  // OAuth (e.g. clicked Amazon on socials but backend current_step is still
  // email). Reading sessionStorage here covers that case.
  useEffect(() => {
    if (!isLive) return;
    setLiveViewStep((prev) => {
      if (prev !== null) return prev;
      let stashed: number | null = null;
      try {
        const raw = sessionStorage.getItem("dv_oauth_return_step");
        if (raw !== null) {
          const parsed = parseInt(raw, 10);
          if (!Number.isNaN(parsed)) {
            const target = Math.min(Math.max(parsed - 1, 0), maxPlanIndex);
            stashed = target;
          }
          // Single-use — clear so subsequent loads don't reuse it.
          sessionStorage.removeItem("dv_oauth_return_token");
          sessionStorage.removeItem("dv_oauth_return_step");
        }
      } catch { /* ignore */ }
      if (stashed !== null) {
        setLiveSkippedThrough((s) => laterInPlan(planIndices, s, stashed!));
        return stashed;
      }
      return furthestStep;
    });
  }, [isLive, furthestStep, planIndices, maxPlanIndex]);

  /**
   * Put the demo walkthrough back to its opening state.
   *
   * `resetApplicantFlow` only clears FlowContext. Two other pieces of demo
   * state live outside it and were surviving a reset: the simulated IDV
   * result, which is component state, and the voucher invitations, which are
   * in sessionStorage under a key SHARED by all three demo journeys
   * (`dv_voucher_invite_demo` — the storageKey in VoucherInviteForm resolves
   * to it for every tokenless session). Pressing Reset therefore cleared the
   * steps and the connections and left the invited voucher sitting there.
   *
   * `invitationsTick` is bumped last because `demoInvitations` re-reads
   * sessionStorage off it; without the bump the list would render from a stale
   * memo until something else moved.
   */
  const resetDemoWalkthrough = useCallback(() => {
    resetApplicantFlow();
    setDemoIdvVerified(false);
    try { sessionStorage.removeItem("dv_voucher_invite_demo"); } catch { /* private mode */ }
    setInvitationsTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A lane switch can leave FlowContext holding a step the ACTIVE plan does not
  // have. `state.currentStep` survives unmount (FlowProvider is above the route
  // table) and `current` is unclamped in demo, so walking `?flow=tech_role` to
  // the IDV screen (index 6) and then opening `?flow=high_volume` — plan
  // [0,1,2,4,5] — leaves current at 6. Two things go wrong: the IDV screen
  // renders on a lane whose definition has no ID step, and `rankOfIndex` scores
  // an out-of-plan 6 at 4.5, which is enough for `isLastInPlan` to call it the
  // last step — so Continue runs `completeStepUp()` and skips email, accounts,
  // vouch and review outright.
  //
  // Restart instead: a step belonging to another lane is not a position in this
  // one. Cannot fire on the legacy plan, whose only out-of-plan index is the
  // hidden work step nothing ever sets.
  useEffect(() => {
    if (isLive) return;
    if (planIndices.includes(state.currentStep)) return;
    resetDemoWalkthrough();
    // `setCurrentStep` is rebuilt every FlowProvider render — including it here
    // would re-run this effect forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive, planIndices, state.currentStep]);

  /**
   * Start this lane's walkthrough clean when the viewer arrives on a DIFFERENT
   * lane than the one they were last on.
   *
   * The clamp above only catches a step the new plan has no screen for, and
   * the steps the lanes share are the ones that hurt: VOUCH is index 4 in both
   * plans, so walking `tech_role` to the vouch screen, exiting, and opening
   * `high_volume` landed straight back on the vouch screen — with the invite
   * from the other lane still listed on it.
   *
   * A ref cannot answer this. Leaving the page unmounts the component (the hub
   * is a different route) while FlowProvider, which sits above the route table,
   * keeps its state — so the marker has to outlive the component too.
   *
   * Reloading the SAME lane deliberately does NOT reset: resuming where you
   * left off is the useful behaviour, and it is only crossing between lanes
   * that carries one journey's answers into another's.
   */
  // `useLayoutEffect`, not `useEffect`: a passive effect runs AFTER paint, so
  // the viewer would see one frame of the other lane's screen — the vouch step,
  // with the other lane's invite on it — before it snapped back to the start.
  // One frame of the exact wrong thing is worse in a demo than anywhere else.
  useLayoutEffect(() => {
    if (isLive) return;
    // Sentinel for "no ?flow=", i.e. the default journey. Deliberately not
    // named after the vertical: publicFlowVocabulary scans every string
    // literal in this file, and it was right to flag the first attempt — a
    // marker value is not a reason to teach the guard a new exception.
    const lane = demoFlow?.key ?? "none";
    let previous: string | null = null;
    try { previous = sessionStorage.getItem(DEMO_LANE_KEY); } catch { /* private mode */ }
    if (previous !== null && previous !== lane) resetDemoWalkthrough();
    try { sessionStorage.setItem(DEMO_LANE_KEY, lane); } catch { /* private mode */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive, demoFlow]);

  const current = isLive ? (liveViewStep ?? furthestStep) : state.currentStep;
  // Demo-only: sync stepper position to ?step=N so refresh/deep-links land where the user left off.
  // In live mode the URL is owned by /verify/:token + the backend, so the sync
  // is disabled outright. (This used to be faked with sentinel args — step -1,
  // a noop setter, paramName "__noop_step__" — which made the hook's init
  // stamp write a junk `?__noop_step__=0` onto every live applicant URL.)
  useStepParam(state.currentStep, setCurrentStep, {
    max: maxPlanIndex,
    enabled: !isLive,
  });
  // "Viewing past" means revisiting a step the backend has already moved past.
  // Locally-skipped steps don't count — the user can still fill them in.
  const isViewingPast =
    isLive && rankOfIndex(planIndices, current) < rankOfIndex(planIndices, backendStep);
  const [selectedWork, setSelectedWork] = useState<string[]>([]);
  // Step 3: both category accordions default to open (independent collapses).
  // Keyed by the `cat.key` strings ("social", "other"). See CLAUDE.md.
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => new Set(["social", "other"]),
  );
  const toggleCategory = (key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  // Headless Plaid trigger for the payroll card's "Try connecting again" ghost
  // button (State B) — it must not look like the primary CTA, so the button
  // renders headlessly and this ref opens Link. Safe to drive from a click here
  // because that click changes no state, so this subtree stays mounted while the
  // link-token request is in flight.
  const retryConnectRef = useRef<(() => void) | null>(null);
  const voucherFormRef = useRef<HTMLDivElement>(null);
  const voucherFormApiRef = useRef<VoucherInviteFormHandle>(null);
  const [pendingSource, setPendingSource] = useState<SourceOption | null>(null);
  const [consenting, setConsenting] = useState(false);
  const [liveSubmitted, setLiveSubmitted] = useState(false);
  const [declineStage, setDeclineStage] = useState<"form" | "confirm" | "declined">("form");
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [showQuickCode, setShowQuickCode] = useState(false);
  // Whether the payroll card has reframed into its upload-fallback state (B),
  // and WHY — the two entry points need different framing. "exit" is the rescue
  // case (UC1: they tried and couldn't); "chosen" is the applicant electing
  // upload up front (UC2), who must not be asked "couldn't connect?" about an
  // attempt they never made. One-way: once someone has seen the fallback we
  // don't yank it away mid-attempt.
  const [docFallbackReason, setDocFallbackReason] = useState<"exit" | "chosen" | null>(null);
  const docFallbackShown = docFallbackReason !== null;
  const revealDocFallback = useCallback((reason: "exit" | "chosen") => {
    if (!SHOW_PAYROLL_DOC_FALLBACK) return;
    setDocFallbackReason((already) => {
      if (already) return already;
      trackPayrollFunnel("payroll_fallback_shown");
      return reason;
    });
  }, []);
  const [declineReason, setDeclineReason] = useState("");
  const [declining, setDeclining] = useState(false);

  // If the loaded session is already declined (terminal), short-circuit the UI
  // to the "declined" screen immediately — same view the applicant saw at
  // confirmation time. Re-opening the magic link should NOT land back on the
  // consent form. (Backend keeps GET /sessions/:token returning declined
  // sessions via getSessionByTokenRaw so the session object exists here.)
  // We also pin the stepper view to step 0 since the declined screen renders
  // there.
  useEffect(() => {
    if (isLive && session?.status === "declined") {
      if (declineStage !== "declined") setDeclineStage("declined");
      setLiveViewStep(0);
    }
  }, [isLive, session?.status, declineStage]);

  // First name for greeting — applicant_name when available, otherwise demo persona, otherwise "there".
  const firstName = (() => {
    const raw = isLive ? session?.applicant_name : "Juan Alvarez";
    if (!raw) return "there";
    return raw.trim().split(/\s+/)[0];
  })();

  const submittedSocialIds = useMemo(
    () => new Set((session?.connected_providers ?? []).map((p) => p.provider.toLowerCase())),
    [session],
  );
  // Was `submittedSocialIds` plus an in-flight set fed by the Facebook
  // submitSocial call, so the row could show "connected" before the session
  // rehydrated. Facebook is an OAuth tile now: the applicant leaves the page
  // entirely and returns to a refreshed session, so there is no in-flight
  // window to paper over and nothing else ever wrote to that set.
  const liveSocialIds = submittedSocialIds;

  const verifiedEmailSignals = useMemo<SignalSummary[]>(() => {
    // `effectiveSession`, not `session`: same object live, and it is what lets
    // a demo email verification surface at all.
    // One-time-code verifications only. `includes("email")` used to match
    // `digital_email_history` too, which every Google, Microsoft, Amazon, Uber
    // or Apple connection writes, so a connection that proved no address read
    // as a verified email (platform#712).
    const fromSignals = (effectiveSession?.signals_summary ?? []).filter(isOtpEmailSignal);

    const fromProviders = (effectiveSession?.connected_providers ?? [])
      .filter((p) => p.provider.toLowerCase() === "email" && p.status === "connected")
      .map<SignalSummary>((p, i) => ({
        key: `contact_email_${i}`,
        label: "Email Verification",
        status: "verified",
        detail: p.masked_identifier,
      }));

    // One code reaches the page twice: as the `contact_email` signal (one
    // entry per signal type, detail "work email (domain)") and as a
    // `provider: "email"` entry (one per verified address, masked). Listing
    // both read as two addresses checked. The address entries are the list;
    // the signal counts only when this session has none (a re-sent link
    // keeps the signal but starts a new address list).
    const seen = new Set<string>();
    return (fromProviders.length > 0 ? fromProviders : fromSignals).filter((s) => {
      const key = (s.detail ?? s.key).toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [effectiveSession]);

  // Smart routing for step 1: pick OAuth-recommended vs OTP-led layout based
  // on the applicant's saved application email. See src/lib/emailRouting.ts
  // and CLAUDE.md. Null = field missing OR malformed → safe fallback layout.
  const applicantEmail = effectiveSession?.applicant_email ?? "";
  const emailRoute = useMemo(() => classifyEmail(applicantEmail), [applicantEmail]);





  // Which step indexes have actual evidence attached (live mode).
  // Used to distinguish "completed with evidence" (green) vs "skipped /
  // unfilled" (amber) when the applicant has moved past a step without
  // contributing anything to it.
  const stepHasEvidence = useMemo(() => {
    const map: Record<number, boolean> = {};
    if (!isLive || !session) return map;
    map[0] = furthestStep >= 1; // consent given iff we've moved past step 0
    const providers = session.connected_providers ?? [];
    const oauthProviders = new Set(["google", "microsoft", "apple", "amazon"]);
    map[1] = Boolean(session.applicant_email)
      || providers.some((p) => oauthProviders.has(p.provider.toLowerCase()))
      || (session.signals_summary ?? []).some((s) => s.key.toLowerCase().includes("email"));
    map[2] = providers.some((p) => !oauthProviders.has(p.provider.toLowerCase()))
      || (session.signals_summary ?? []).some((s) => /linkedin|github|x|twitter|tiktok|instagram|facebook|reddit/i.test(s.key));
    map[3] = (session.signals_summary ?? []).some(
      (s) => /payroll|income|employ|work/i.test(s.key) && s.status === "verified",
    );
    map[4] = (session.signals_summary ?? []).some((s) => /voucher|reference/i.test(s.key))
      || (session.pending_vouchers_count ?? 0) > 0;
    map[5] = session.status === "completed";
    // Index 6 — the definition-driven IDV screen. Reads the same two fields the
    // step-1 IDV card does, plus the recorded step, because `plaid_idv` can be
    // written out of band by the Plaid webhook or the reconciliation sweep long
    // after the applicant left the page.
    map[APPLICANT_STEP.IDV] = isIdvSatisfied(session);
    // On a definition plan, the definition's own `satisfied_by` overrides the
    // provider heuristics above for every step it covers — same predicate the
    // server gates on, so the pip and the gate cannot disagree.
    //
    // The heuristics were written for the leasing flow and had drifted: they
    // read Amazon as EMAIL evidence and refused it as digital-account
    // evidence, while `tech_role` v2 splits the two the other way (email =
    // Google/Microsoft/Apple, digital account = LinkedIn/Amazon). Connecting
    // Amazon for the required digital-account step therefore turned the email
    // pip green and left the digital-account pip amber — over a step the server
    // had already counted as satisfied.
    //
    // Only the definition's own steps are overridden. The vouch and review
    // screens are not workflow steps, so nothing in `steps_completed` names
    // them and their entries above stand.
    if (definitionPlan) {
      Object.assign(map, definitionEvidenceByIndex(definitionPlan, stepsCompleted));
    }
    return map;
  }, [isLive, session, furthestStep, definitionPlan, stepsCompleted]);

  // Demo-only: show resume gate when in a resume phase
  if (!isLive && (state.resumePhase === "verify" || state.resumePhase === "verifying" || state.resumePhase === "restoring" || state.resumePhase === "resumed")) {
    return <ResumeGate />;
  }

  // Live-mode loading / error states
  if (isLive && loading && !session) {
    return (
      <div className="max-w-md mx-auto mt-16">
        <Card><CardContent className="p-8 text-center space-y-3">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading your application…</p>
        </CardContent></Card>
      </div>
    );
  }

  if (isLive && error) {
    // Treat as "expired link" when:
    //  - 410 Gone or 404 Not Found (session token unknown / removed)
    //  - 401 Unauthorized with INVALID_AUTH (backend's response when the
    //    session token has passed its 72h expiry — it filters by
    //    expires_at > now() and returns no row, which the auth layer maps
    //    to INVALID_AUTH).
    const expired =
      error.status === 410 ||
      error.status === 404 ||
      (error.status === 401 && (error.code === "INVALID_AUTH" || error.code === "SESSION_EXPIRED"));
    return (
      <div className="max-w-md mx-auto mt-16">
        <Card><CardContent className="p-8 text-center space-y-3">
          <AlertTriangle className="w-10 h-10 text-trust-amber-foreground mx-auto" />
          <h2 className="text-lg font-display font-semibold text-foreground">
            {expired ? "This verification link has expired" : "We couldn't load your application"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {expired
              ? "For your security, verification links expire after 72 hours. Please request a new link from the organization that sent you this invitation."
              : error.message}
          </p>
          {expired && (
            <p className="text-xs text-muted-foreground pt-2">
              If you've already completed your application, no further action is needed.
            </p>
          )}
          {!apiBaseConfigured() && (
            <p className="text-xs text-destructive">VITE_API_URL is not configured.</p>
          )}
        </CardContent></Card>
      </div>
    );
  }

  // Map a row id (e.g. "linkedin", "facebook", "amazon", "uber", "google-cross")
  // to the backend provider key it corresponds to.
  //
  // ⚠️ Facebook used to be special-cased to "instagram" here, because that was
  // the key the manual-submit path sent. It is a real OAuth provider now
  // (platform #424) and its row id IS its provider key, like every other row.
  // The manual path itself is gone (platform #442).
  const rowIdToProvider = (id: string): string =>
    id.replace(/-cross$/, "").toLowerCase();

  const isLiveConnected = (id: string): boolean => {
    if (!session) return false;
    const provider = rowIdToProvider(id);
    if ((session.connected_providers ?? []).some(
      (p) => p.provider.toLowerCase() === provider && p.status === "connected",
    )) return true;
    return (session.signals_summary ?? []).some(
      (s) => s.status === "verified" && s.key.toLowerCase().includes(provider),
    );
  };

  const isConnected = (id: string) =>
    isLive ? isLiveConnected(id) : state.connectedSources.some((s) => s.id === id);

  const handleSourceClick = (src: SourceOption) => {
    if (src.comingSoon) return;
    if (isLive && src.liveOAuth) {
      void startOAuth(src.liveOAuth);
      return;
    }
    if (isConnected(src.id)) {
      disconnectSource(src.id);
    } else {
      setPendingSource(src);
    }
  };

  /**
   * Demo click on an email provider row (step 1).
   *
   * Routes through the SAME confirm dialog and `connectSource` the account
   * tiles on step 2 use. It used to push the tile's display name onto a local
   * `selectedAccounts` array instead — a tick, not a connection — so a Google
   * "connection" made here never reached FlowContext, never appeared in the
   * step-5 summary, and never became a `connected_provider` on the synthetic
   * session. Two demo mechanisms for one gesture, and only one of them counted.
   *
   * That array also keyed the account-check count off `name.toLowerCase()`,
   * which is `"microsoft / outlook / hotmail / live"` for the Microsoft tile —
   * never equal to `"microsoft"`, so selecting it silently counted for
   * nothing. Keying off `provider` fixes that by construction.
   */
  const handleEmailProviderClick = (opt: AccountTile) => {
    if (opt.comingSoon || !opt.provider) return;
    if (isConnected(opt.provider)) {
      disconnectSource(opt.provider);
      return;
    }
    setPendingSource({
      id: opt.provider,
      name: opt.name,
      desc: opt.desc,
      category: "other",
      tenure: "—",
      corroboration: "Email ownership verified",
      strength: "moderate",
    });
  };

  const confirmConnect = () => {
    if (!pendingSource) return;
    connectSource({
      id: pendingSource.id,
      category: pendingSource.category,
      name: pendingSource.name,
      tenure: pendingSource.tenure ?? "—",
      corroboration: pendingSource.corroboration ?? "",
      strength: pendingSource.strength,
    });
    setPendingSource(null);
  };

  const startOAuth = async (provider: "google" | "microsoft" | "linkedin" | "amazon" | "uber" | "apple" | "facebook" | "github") => {
    if (!token) return;
    try {
      const { redirect_url } = await applicantApi.startOAuth(token, provider);
      // Stash the verify token + the step the user was on so the OAuth callback
      // page can route them back to the right session AND the right step, even
      // if the backend redirects to a generic /oauth-success / /oauth-error path.
      try {
        sessionStorage.setItem("dv_oauth_return_token", token);
        sessionStorage.setItem("dv_oauth_return_step", String((liveViewStep ?? furthestStep) + 1));
      } catch { /* ignore */ }
      // PUSH, never replace. `replace()` overwrites the CURRENT entry — ours —
      // so the applicant's verification page stops existing. Opened from an
      // email link in a fresh tab, Back from the provider then lands on the
      // browser's home page and the application is gone (UAT 2026-08-03).
      //
      // The trade-off, taken deliberately: after a successful sign-in, Back
      // from the success page reaches the provider's URL with a consumed
      // state. Every OAuth redirect on the web behaves that way, and the
      // applicant is on a success page — not stranded mid-flow.
      window.location.href = redirect_url;
    } catch (e) {
      toast({ title: `Could not start ${oauthProviderKeyName(provider)} sign-in`, description: (e as ApiError).message, variant: "destructive" });
    }
  };

  const handleConsent = async () => {
    if (!token) return;
    setConsenting(true);
    try {
      await applicantApi.giveConsent(token);
      await refresh();
      // Auto-advance to whatever the plan puts after consent. On the legacy
      // flow that is index 1 (email), exactly as before; on `tech_role` it is
      // the IDV screen, because the definition orders it second.
      const afterConsent = nextIndexInPlan(planIndices, APPLICANT_STEP.CONSENT);
      setLiveSkippedThrough((prev) => laterInPlan(planIndices, prev, afterConsent));
      setLiveViewStep(afterConsent);
      toast({ title: "Consent recorded", description: "Continuing to the next step." });
    } catch (e) {
      toast({ title: "Could not record consent", description: (e as ApiError).message, variant: "destructive" });
    } finally { setConsenting(false); }
  };

  const handleNext = async () => {
    if (isLive) {
      // If viewing a past (already-reached) step, advance skipping hidden steps.
      if (rankOfIndex(planIndices, current) < rankOfIndex(planIndices, furthestStep)) {
        setLiveViewStep(nextIndexInPlan(planIndices, current));
        return;
      }
      // At the frontier: locally skip forward so the user can keep moving even
      // if the backend hasn't recorded progress yet. The step stays amber
      // ("unfilled") in the stepper. Don't skip past the final review step.
      // nextIndexInPlan walks the plan, which is the hardcoded visible list on
      // the legacy path and the definition's order on a role-typed session.
      if (!isLastInPlan(planIndices, current)) {
        const next = nextIndexInPlan(planIndices, current);
        setLiveSkippedThrough((prev) => laterInPlan(planIndices, prev, next));
        setLiveViewStep(next);
        return;
      }
      // At the review step, refresh from backend. Toast the outcome so the
      // applicant has visual confirmation that the button did something.
      const ok = await refresh();
      if (ok) {
        toast({ title: "Status refreshed", description: "Latest verification status loaded." });
      } else {
        toast({
          title: "Couldn't refresh",
          description: "Please try again in a moment.",
          variant: "destructive",
        });
      }
      return;
    }
    if (isLastInPlan(planIndices, current)) { completeStepUp(); return; }
    setCurrentStep(nextIndexInPlan(planIndices, current));
  };

  // In live mode, derive the count from real session data (connected_providers
  // + signals_summary) so OAuth/SDK/manual social connections all count. The
  // mock branch is preserved for non-live demo mode.
  const liveConnectedCount = (() => {
    if (!isLive || !session) return 0;
    const ids = new Set<string>();
    for (const p of session.connected_providers ?? []) {
      const id = p.provider.toLowerCase();
      if (p.status !== "connected") continue;
      if (id === "email") continue; // email belongs to step 2
      ids.add(id);
    }
    for (const s of session.signals_summary ?? []) {
      if (s.status !== "verified") continue;
      const m = s.key.toLowerCase().match(/linkedin|github|tiktok|instagram|facebook|reddit|twitter|amazon|uber/);
      if (m) ids.add(m[0]);
    }
    return ids.size;
  })();
  const connectedCount = isLive ? liveConnectedCount : state.connectedSources.length;
  // Account-check tier counter for vouch requirement + tracker pips.
  // Counts any connected account EXCEPT email OTP and Plaid — those remain
  // supporting evidence but do not move the 1/2/3+ vouch tier.
  const NON_OAUTH_ACCOUNT_IDS = new Set(["email", "plaid"]);
  // No `pendingProviderIds`: the only caller-supplied ids were the Facebook
  // submitSocial keys, and that path is gone. An OAuth connection is already in
  // `connected_providers` by the time the applicant is back on this page.
  const accountChecksCount = isLive
    ? countConnectedOAuth(session)
    : (() => {
        // Demo: count any connected OAuth source, excluding email OTP and
        // Plaid. Step-1 email providers land in `connectedSources` under their
        // provider id now (see handleEmailProviderClick), so they are counted
        // by the loop below rather than by a second, separate list.
        const ids = new Set<string>();
        for (const s of state.connectedSources) {
          const id = s.id.toLowerCase();
          if (!NON_OAUTH_ACCOUNT_IDS.has(id)) ids.add(id);
        }
        return ids.size;
      })();

  // The pieces of evidence the tracker counts: the server's own count when it
  // publishes one (it also counts verified email addresses, which the browser
  // cannot), else connected accounts.
  const evidencePiecesCount = serverRule?.evidencePieces ?? accountChecksCount;
  // At most 3 of the 4 tracks come from evidence: at least one must be a vouch
  // invite, so a full tracker never sits over a Submit that stays shut.
  const verificationTracksFilled = Math.min(
    Math.min(evidencePiecesCount, 3) + submission.sentVouchInvites,
    4,
  );
  const verificationTrackerReady = verificationTracksFilled >= 4;
  // The server's numbers (`numbersRule`, set above) or, on an older backend,
  // the account tier: what the vouch screen asks for and when Submit opens.
  // On the account tier the gate needs a piece and enough invites and nothing
  // else, so the page may open Submit the moment the last invite goes out rather
  // than wait for the refresh; Submit itself is still checked by the server.
  const tierOpensLocally =
    serverRule !== null &&
    session?.vouch_summary?.required_by === "account_tier" &&
    typeof session?.vouch_summary?.required === "number" &&
    submission.sentVouchInvites >= session.vouch_summary.required;
  const submitReady = isSubmitReady(numbersRule, verificationTracksFilled) || tierOpensLocally;
  const requiredInvites = requiredVouchInvites(numbersRule, accountChecksCount);
  // A disabled Continue must say why, in words a screen reader reaches too.
  // The toast in its onClick can never fire while it is disabled. Flow
  // sessions only: the leasing screen is left exactly as it was.
  const continueBlockedReason = (blocked: boolean, required: number) => {
    if (!flowRule || !blocked) return null;
    const needed = Math.max(0, required - submission.sentVouchInvites);
    return (
      <p id="vouch-continue-reason" className="mt-2 text-center text-xs text-muted-foreground">
        {continueBlockedMessage(needed, submission.sentVouchInvites)}
      </p>
    );
  };
  const connectedInCategory = (sources: SourceOption[]) => sources.filter((s) => isConnected(s.id)).length;

  const stepUpRequired = isLive && session?.step_up_required;

  const completed = (!isLive && state.stepUpCompleted) || (isLive && (session?.status === "completed" || liveSubmitted));

  // Article keeps the final state as quiet as the rest of its reskinned
  // journey: one confirmation mark, one message, and one supporting sentence.
  // In particular, the tokenless implementation route must not turn the
  // production completion state back into a demo control panel.
  if (articlePresentation && completed) {
    const done = liveCompletionScreen(copy, firstName, effectiveSession?.org_name);
    // `mx-auto` as on every other step's wrapper: without it the 35rem column
    // sat against the left edge on a wide window, so "Your update is with the
    // hiring team" read off-centre (review call 2026-10-15).
    return (
      <div className="article-flow article-applicant-flow article-complete-screen mx-auto">
        {implementationMode && <p className="demo-preview-note" role="note">{PREVIEW_ROUTE_NOTE}</p>}
        <div className="article-complete" role="status">
          <CheckCircle2 className="article-complete-icon" aria-hidden="true" />
          <h1 className="article-complete-title">{done.title}</h1>
          <p className="article-complete-copy">{done.body.join(" ")}</p>
          {/* Same two lines as the original screen: the copy deck's footnote
              and when the update was submitted. */}
          {done.footnote && <p className="article-complete-footnote">{done.footnote}</p>}
          {effectiveSession?.submitted_at && (
            <p className="article-complete-footnote">
              Submitted on {new Date(effectiveSession.submitted_at).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Live mode: when the session is already completed (or just submitted),
  // show only the completion screen. No header, no stepper, no other content.
  //
  // ⚠️ This `return` shadows every later branch on the same condition, which
  // is how the vertical's completion copy (`checkpointIntro.complete`, read
  // further down) went unreachable in live mode. The copy is decided in
  // `liveCompletionScreen` so this branch, the one that actually renders,
  // is the one that reads the vertical.
  if (isLive && (session?.status === "completed" || liveSubmitted)) {
    const done = liveCompletionScreen(copy, firstName, session?.org_name);
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <Card className="bg-trust-green-muted border-trust-green/20 shadow-sm">
            <CardContent className="p-8 text-center space-y-4">
              <CheckCircle2 className="w-12 h-12 text-trust-green mx-auto" />
              <h2 className="text-xl font-display font-bold text-foreground">{done.title}</h2>
              {done.body.map((line) => (
                <p key={line} className="text-sm text-muted-foreground">{line}</p>
              ))}
              {done.footnote && <p className="text-xs text-muted-foreground">{done.footnote}</p>}
              {session?.submitted_at && (
                <p className="text-xs text-muted-foreground">
                  Submitted on {new Date(session.submitted_at).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (showStep1NewPreview) {
    return <ApplicantFlowStep1New />;
  }

  return (

    <div className={`max-w-2xl mx-auto space-y-6 ${articlePresentation ? "article-flow article-applicant-flow" : ""}`}>
      {implementationMode && <p className="demo-preview-note" role="note">{PREVIEW_ROUTE_NOTE}</p>}
      {!isLive && !implementationMode && (
        <>
          <div className="rounded-lg border border-violet-300/60 bg-violet-50/70 px-4 py-3 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-violet-700 shrink-0 mt-0.5" aria-hidden />
            <div className="flex-1">
              <div className="text-sm font-semibold text-violet-900">Demo mode · Applicant View</div>
              <p className="text-xs text-violet-900/80 mt-0.5">
                This is a sample applicant flow for demonstration. No actions are saved.
              </p>
            </div>
            {/* Back to /demo, the hub this was opened from — not /dashboard.
                Three reasons, and the second is the one that bites: the viewer
                came from the hub and is usually browsing more than one demo;
                "Verification Center" is the LEASING queue, so an operator
                walking an IDW lane was being shown the other vertical's
                surface as the way out; and a hardcoded /dashboard bypasses
                `OperatorHomeRedirect`, which is the same bug #175 fixed in
                CoBrandedHeader. /demo sidesteps all three — it is where they
                started and it has no vertical. */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/demo")}
              className="shrink-0 bg-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Exit demo
            </Button>
          </div>
        </>
      )}
      {/* Confirmation Dialog (demo) */}
      <Dialog open={!!pendingSource} onOpenChange={(open) => !open && setPendingSource(null)}>
        <DialogContent className={articlePresentation ? "article-dialog sm:max-w-md" : "sm:max-w-md"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-trust-green" />
              Connect {pendingSource?.name}
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>
                We only verify <span className="font-medium text-foreground">account ownership and basic metadata</span>.
              </p>
              <div className="bg-muted/50 rounded-lg p-3 space-y-1.5 text-xs">
                <p className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-trust-green shrink-0" /> We confirm account ownership and continuity</p>
                <p className="flex items-center gap-2"><X className="w-3.5 h-3.5 text-destructive shrink-0" /> We do not read messages, posts, or personal content</p>
                <p className="flex items-center gap-2"><X className="w-3.5 h-3.5 text-destructive shrink-0" /> We do not store passwords or credentials</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPendingSource(null)}>Cancel</Button>
            <Button onClick={confirmConnect}>
              <ShieldCheck className="w-4 h-4 mr-1" /> Connect securely
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="article-flow-intro text-center space-y-2">
        {articlePresentation && (() => {
          const position = Math.max(0, planIndices.indexOf(current));
          const previous = planIndices[position - 1];
          // Same count as the original stepper: our screens only.
          const { position: displayPosition, total: displayTotal } = stepCounter(planIndices, current);
          const stepLabel = ({
            [APPLICANT_STEP.CONSENT]: "Your Trust Checkpoint",
            [APPLICANT_STEP.EMAIL]: "Email",
            [APPLICANT_STEP.ACCOUNTS]: "Digital accounts",
            [APPLICANT_STEP.VOUCH]: "People who know you",
            [APPLICANT_STEP.REVIEW]: "Review",
            [APPLICANT_STEP.IDV]: featherPresentation ? "ID & biometrics" : "Identity",
          } as Record<number, string>)[current] ?? planEntryFor(current).title;
          return (
            <>
              <div className="article-flow-meta">
                <button
                  type="button"
                  // No screen before the first one. Going back in browser
                  // history from here can land on the new-tab page and lose
                  // the application, so Back stays disabled instead.
                  disabled={previous === undefined}
                  onClick={() => {
                    if (previous === undefined) return;
                    if (isLive) setLiveViewStep(previous); else setCurrentStep(previous);
                  }}
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <span>{stepLabel} <strong>{displayPosition} / {displayTotal}</strong></span>
              </div>
              <div className="article-flow-progress" role="progressbar" aria-label="Application progress" aria-valuemin={1} aria-valuemax={displayTotal} aria-valuenow={displayPosition}>
                {Array.from({ length: displayTotal }, (_, index) => (
                  <span key={index} className={index < displayPosition ? "is-filled" : ""} />
                ))}
              </div>
            </>
          );
        })()}
        {!articlePresentation && <div className="flex items-center justify-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-2 bg-trust-blue-muted text-trust-blue px-3 py-1 rounded-full text-xs font-medium">
            <Shield className="w-3.5 h-3.5" /> {isLive ? "Live application" : implementationMode ? `${checkpointOrg} applicant` : "Applicant View"}
          </div>
          {isLive && session && (
            <div className="inline-flex items-center gap-1.5 bg-muted text-muted-foreground px-2.5 py-1 rounded-full text-[11px] font-medium">
              {session.org_name}
            </div>
          )}
          {!isLive && (
            <>
              <Button
                variant="ghost"
                size="sm"
                // Also clears the simulated IDV result, which lives here rather
                // than in FlowContext. Without this, "Start over" returns the
                // walkthrough to step 0 with the ID check still showing as
                // passed — and no way back to the unverified state short of a
                // full reload.
                onClick={resetDemoWalkthrough}
                className="text-xs text-muted-foreground gap-1"
              >
                <RotateCcw className="w-3 h-3" /> Reset
              </Button>
              {!implementationMode && !state.stepUpCompleted && current > 0 && (
                <Button variant="ghost" size="sm" onClick={simulateLeaveAndReturn} className="text-xs text-trust-amber-foreground gap-1">
                  <LogOut className="w-3 h-3" /> Simulate: Leave & Return
                </Button>
              )}
            </>
          )}
        </div>}
        {!articlePresentation && <div className="pt-4" />}
        {/* Restored 2026-09-01 after being removed earlier in this branch —
            Juan is replacing the wording per step, so the structure stays.
            Two variants: step 0 gets the welcome and the time estimate, every
            later step gets the continuation line. */}
        {(() => {
          // Each screen leads with its own sentence in the v6 mockups. Page 1
          // uses the top-level headline/subhead; later steps look themselves up
          // and fall back to the generic pair.
          const perStep = checkpoint?.pageHeaders[current];
          const onConsent = current === 0 && declineStage === "form";
          // The completed screen has its own pair and is not a step, so it is
          // resolved before the step lookup rather than through it.
          const done = (!isLive && state.stepUpCompleted) || (isLive && (session?.status === "completed" || liveSubmitted));
          if (done && checkpoint) {
            return (
              <>
                <h1 className="text-2xl font-display font-bold text-foreground">{checkpoint.complete.h1}</h1>
                <p className="text-sm text-muted-foreground max-w-lg mx-auto">{checkpoint.complete.h2}</p>
              </>
            );
          }
          const articleHeader = articlePresentation ? ({
            [APPLICANT_STEP.CONSENT]: {
              h1: `Help the ${copy.reviewingTeam} get to know you.`,
              h2: `Help the ${copy.reviewingTeam} see the person behind your application.`,
            },
            [APPLICANT_STEP.EMAIL]: { h1: "Confirm the email you applied with.", h2: "Choose your email provider or use a quick code." },
            [APPLICANT_STEP.ACCOUNTS]: { h1: "A little more you.", h2: "Connect an account you already use to help confirm it’s you." },
            [APPLICANT_STEP.VOUCH]: { h1: "Invite people who know you.", h2: "Ask someone who can confirm how they know you." },
            [APPLICANT_STEP.REVIEW]: { h1: "Review what you share.", h2: `Check the details before sharing them with ${checkpointOrg}.` },
            [APPLICANT_STEP.IDV]: featherPresentation
              ? { h1: "Optional ID & biometrics.", h2: "Use a government-issued ID and a quick selfie to add another trust signal." }
              : { h1: "Confirm your identity.", h2: "A quick ID check connects this application to you." },
          } as Record<number, { h1: string; h2: string }>)[current] : null;
          const h1 = articleHeader?.h1 ?? (onConsent
            ? (checkpoint?.headline ??
                (firstName === "there"
                  ? "You're already off to a great start"
                  : `${firstName}, you're already off to a great start`))
            : (perStep?.h1 ??
                (firstName === "there"
                  ? "Hi there, help us confirm a few more details"
                  : `${firstName}, help us confirm a few more details`)));
          const h2 = articleHeader?.h2 ?? (onConsent
            ? (checkpoint?.subhead ??
                `${effectiveSession?.org_name ?? `The ${copy.reviewingTeam}`} already has part of what they need to review your application. This step helps complete your identity review so they can keep your application moving.`)
            : (perStep?.h2 ??
                "We already have part of what we need. To keep your application moving, we may ask you to connect a few accounts to confirm your identity."));
          return (
            <>
              <h1 className="text-2xl font-display font-bold text-foreground">{h1}</h1>
              <p className="text-sm text-muted-foreground max-w-lg mx-auto">{h2}</p>
            </>
          );
        })()}
      </motion.div>

      {/* Stepper */}
      {!articlePresentation && <div className="flex items-center justify-center gap-1">
        {planIndices.map((i, displayPos) => {
          // Rank, not raw index: on a definition plan the later step is not the
          // higher index. Identical to `i <= furthestStep` / `i < furthestStep`
          // on the legacy plan, whose ranks increase with index.
          const rank = rankOfIndex(planIndices, i);
          const furthestRank = rankOfIndex(planIndices, furthestStep);
          const reachable = isLive ? rank <= furthestRank : true;
          const completed = isLive
            ? rank < furthestRank
            : rank < rankOfIndex(planIndices, current);
          const isActive = i === current;
          // "Step N of M", from the helper the Article meta row reads too.
          const counter = stepCounter(planIndices, i);
          // Once the verification tracker is fully satisfied (account checks +
          // sent vouches ≥ 4), any previously-skipped intermediate step is
          // promoted to "complete" visually — the applicant has cleared all
          // criteria, so an amber pip would misrepresent their actual state.
          // Never on a Flow: there the pips read the server's own step
          // evidence, and amber over a skipped optional step is the truth.
          const allCriteriaMet = isLive && promotesSkippedSteps(flowRule, verificationTrackerReady);
          // In live mode, a "completed" step with no evidence attached is
          // treated as skipped/unfilled (amber) instead of green — unless
          // overall criteria are met.
          const skipped =
            isLive && completed && !stepHasEvidence[i] && !allCriteriaMet;
          const baseColor = completed
            ? skipped
              ? "bg-trust-amber text-trust-amber-foreground"
              : "bg-trust-green text-trust-green-foreground"
            : isActive
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground";
          const activeRing = isActive ? "ring-4 ring-primary/40 ring-offset-2 ring-offset-background scale-110" : "";
          const hoverRing = !isActive && completed
            ? skipped ? "hover:ring-2 hover:ring-trust-amber/40" : "hover:ring-2 hover:ring-trust-green/40"
            : !isActive && !completed ? "hover:bg-muted-foreground/20" : "";
          return (
            <div key={i} className="flex items-center gap-1">
              <button
                onClick={() => {
                  if (i === current) return;
                  if (isLive) {
                    if (reachable) setLiveViewStep(i);
                  } else {
                    setCurrentStep(i);
                  }
                }}
                disabled={isLive && !reachable}
                aria-label={`Go to step ${counter.position} of ${counter.total}: ${plan[displayPos].title}${skipped ? " (unfilled)" : completed ? " (completed)" : ""}`}
                title={skipped ? "Unfilled — no evidence added" : completed ? "Completed" : isActive ? "Current step" : ""}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${baseColor} ${activeRing} ${hoverRing} ${isLive && !reachable ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
              >
                {completed
                  ? skipped
                    ? <span className="text-xs leading-none">–</span>
                    : <CheckCircle2 className="w-4 h-4" />
                  : counter.position}
              </button>
              {displayPos < planIndices.length - 1 && (
                <div className={`w-6 h-0.5 ${
                  rank < furthestRank
                    ? (stepHasEvidence[i] || allCriteriaMet ? "bg-trust-green" : "bg-trust-amber/60")
                    : "bg-border"
                }`} />
              )}
            </div>
          );
        })}
      </div>}

      {/* Current step content */}
      {(!isLive && state.stepUpCompleted) || (isLive && (session?.status === "completed" || liveSubmitted)) ? (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <Card className="bg-trust-green-muted border-trust-green/20 shadow-sm">
            <CardContent className="p-8 text-center space-y-4">
              <CheckCircle2 className="w-12 h-12 text-trust-green mx-auto" />
              <h2 className="text-xl font-display font-bold text-foreground">
                {checkpoint ? checkpoint.complete.cardTitle : "Additional evidence submitted"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {checkpoint
                  ? checkpoint.complete.cardBody
                  : "Your confidence profile has improved. The review team now has additional corroboration to support your application."}
              </p>
              {checkpoint && (
                <p className="text-xs text-muted-foreground">{checkpoint.complete.cardFootnote}</p>
              )}
              {!isLive && connectedCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {connectedCount} supporting {connectedCount === 1 ? "source" : "sources"} connected
                </p>
              )}
              {!isLive && (
                <div className="flex gap-3 justify-center pt-2">
                  {implementationMode ? (
                    <Button variant="outline" onClick={resetDemoWalkthrough}>
                      <RotateCcw className="w-4 h-4 mr-1" /> Start over
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={() => navigate("/demo")}>
                      <ArrowLeft className="w-4 h-4 mr-1" /> Back to demos
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <motion.div className="article-step-shell" key={current} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
          <Card className="article-step-card bg-card shadow-sm border-border/60">
            <CardContent className="article-step-content p-6 space-y-5">
              {!(current === 0 && declineStage === "form") && (
                <div className="article-step-heading flex items-center gap-3">
                  {(() => { const Icon = planEntryFor(current).icon; return <div className="w-10 h-10 rounded-lg bg-trust-blue-muted flex items-center justify-center"><Icon className="w-5 h-5 text-trust-blue" /></div>; })()}
                  <div className="flex-1 min-w-0">
                    {/* Same weight and size as step 0's in-card heading, which was
                        the only one styled this way. */}
                    <h2 className="text-lg font-semibold text-foreground">{planEntryFor(current).title}</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">{planEntryFor(current).desc}</p>
                  </div>
                  {isViewingPast && !((current === 1 || current === 2 || current === 4) && stepHasEvidence[current]) && (
                    stepHasEvidence[current] ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-trust-green bg-trust-green-muted px-2 py-1 rounded-full whitespace-nowrap">
                        <CheckCircle2 className="w-3 h-3" /> Completed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-trust-amber-foreground bg-trust-amber-muted px-2 py-1 rounded-full whitespace-nowrap">
                        <AlertTriangle className="w-3 h-3" /> Unfilled
                      </span>
                    )
                  )}
                </div>
              )}

              {isViewingPast && current !== 0 && current !== 1 && current !== 2 && current !== 4 && current !== APPLICANT_STEP.IDV && (
                <div className={`border rounded-lg px-3 py-2 text-xs ${
                  stepHasEvidence[current]
                    ? "bg-muted/40 border-border/60 text-muted-foreground"
                    : "bg-trust-amber-muted/60 border-trust-amber/30 text-trust-amber-foreground"
                }`}>
                  {stepHasEvidence[current]
                    ? "You're viewing a completed step. You can still add more optional evidence here to strengthen your application."
                    : "You skipped this step earlier. You can still add evidence here if you'd like."}
                </div>
              )}

              {/* Step 0: Identity / Consent */}
              {current === 0 && declineStage === "confirm" && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {checkpoint ? checkpoint.exit.title : "Decline identity verification?"}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2">
                      {checkpoint
                        ? checkpoint.exit.body
                        : `If you decline, this verification request from ${effectiveSession?.org_name ?? `the ${copy.reviewingTeam}`} will end and no identity signals will be shared. You can't undo this from here.`}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="decline-reason" className="text-xs font-medium text-muted-foreground">
                      Tell us why (optional)
                    </label>
                    <Textarea
                      id="decline-reason"
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value.slice(0, 500))}
                      placeholder={copy.declineReasonPlaceholder}
                      rows={3}
                      maxLength={500}
                      disabled={declining}
                    />
                    <p className="text-xs text-muted-foreground text-right">
                      {declineReason.length}/500
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setDeclineStage("form")}
                      disabled={declining}
                    >
                      {checkpoint ? checkpoint.exit.cancelCta : "Go back"}
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      disabled={declining}
                      onClick={async () => {
                        if (!isLive) {
                          // Demo mode: no backend call.
                          setDeclineStage("declined");
                          return;
                        }
                        setDeclining(true);
                        try {
                          await applicantApi.declineConsent(token, declineReason.trim() || undefined);
                          setDeclineStage("declined");
                        } catch (e) {
                          toast({
                            title: "Could not record decline",
                            description: (e as ApiError).message,
                            variant: "destructive",
                          });
                        } finally {
                          setDeclining(false);
                        }
                      }}
                    >
                      {declining && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                      {checkpoint ? checkpoint.exit.confirmCta : "Confirm decline"}
                    </Button>
                  </div>
                </div>
              )}
              {current === 0 && declineStage === "declined" && (
                <div className="space-y-3 text-center py-4">
                  <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <X className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Verification declined</h3>
                  <p className="text-sm text-muted-foreground">
                    You've declined the identity verification request{isLive && session?.org_name ? ` from ${session.org_name}` : ""}. If you change your mind, please contact the {copy.reviewingTeam} directly to request a new invitation.
                  </p>
                </div>
              )}
              {current === 0 && declineStage === "form" && articlePresentation && (
                <div className="article-checkpoint-intro">
                  <ul className="article-checkpoint-list" aria-label="Trust Checkpoint overview">
                    <li><ShieldCheck aria-hidden="true" /><span>Confirm your details</span></li>
                    <li><UserPlus aria-hidden="true" /><span>Invite people who know you</span></li>
                    <li><FileCheck aria-hidden="true" /><span>Review what you share</span></li>
                  </ul>

                  <p className="article-checkpoint-time">About 5 minutes. One step at a time.</p>

                  {!isViewingPast && (
                    <div className="article-consent-block">
                      <label className="article-consent-row">
                        {/* Named explicitly: the checkbox is a <button>, and
                            Chrome gave it no name from the wrapping label, so
                            a screen reader heard only "checkbox" (Feather UAT
                            walk, 2026-10-14). */}
                        <Checkbox
                          checked={consentAccepted}
                          onCheckedChange={(v) => setConsentAccepted(v === true)}
                          aria-labelledby="applicant-consent-sentence"
                        />
                        <span id="applicant-consent-sentence">{consentSentence.beforeOrg} {consentSentence.org} {consentSentence.afterOrg}</span>
                      </label>
                      <div className="article-policy-links">
                        <a href="https://digavouch.com/user-policy" target="_blank" rel="noopener noreferrer">User Policy</a>
                        <span aria-hidden="true">·</span>
                        <a href="https://digavouch.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                      </div>

                      <Button
                        className="article-primary-action w-full"
                        onClick={isLive ? handleConsent : () => setCurrentStep(nextIndexInPlan(planIndices, APPLICANT_STEP.CONSENT))}
                        disabled={!consentAccepted || (isLive && consenting)}
                      >
                        {isLive && consenting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                        Start Trust Checkpoint <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>

                      {partnerTenantLook || !checkpoint ? (
                        <button
                          type="button"
                          onClick={() => setDeclineStage("confirm")}
                          className="article-standard-review"
                        >
                          Continue standard review
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDeclineStage("confirm")}
                          className="article-checkpoint-optional text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                        >
                          {checkpoint.footerNote}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {current === 0 && declineStage === "form" && !articlePresentation && (
                <div className="space-y-5">
                  <div className="space-y-1">
                    <h3 className="text-lg font-semibold text-foreground">
                      {checkpoint ? checkpoint.cardTitle : `Hi ${firstName}, let's confirm your identity`}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {checkpoint ? checkpoint.cardSubhead : "A few quick steps to help keep your application moving."}
                    </p>
                  </div>

                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-trust-green-muted text-trust-green text-xs font-medium">
                    <Clock className="w-3.5 h-3.5" /> Takes about 5 minutes
                  </div>

                  <ol className="space-y-4">
                    {(definitionPlan
                      // Definition-driven: preview the flow the org actually
                      // configured. The hardcoded list below would promise a
                      // `tech_role` applicant no ID check and a `high_volume`
                      // applicant a required digital account — both wrong, and
                      // the whole point of ADR-014 is that this is data.
                      // Consent is dropped (they are reading it right now).
                      ? definitionPlan
                          .filter((entry) => entry.index !== APPLICANT_STEP.CONSENT)
                          .map((entry) => ({
                            icon: entry.icon,
                            // Page 1 has its own wording for these; the plan
                            // entry is the fallback. See `checkpointIntro
                            // .stepList` for why this is not the step map.
                            title: checkpoint?.stepList[entry.index]?.title ?? entry.title,
                            // No " Optional." suffix. Patrick's rule, 2026-09-01:
                            // the optional choice is whether to do the Trust
                            // Checkpoint at all, not whether to do a step inside
                            // it — so an optional STEP must not read as skippable
                            // once the applicant is in. The step is still
                            // optional in the definition and still ungated.
                            body: checkpoint?.stepList[entry.index]?.body ?? entry.desc,
                          }))
                      : [
                      {
                        icon: Mail,
                        title: "Verify your email",
                        body: "Google, Microsoft, or a quick code",
                      },
                      {
                        icon: Shield,
                        title: "Add identity context",
                        body: "Connect an approved account, invite a vouch, or both",
                      },
                      // Payroll verification (Patrick, 2026-08-03). GATED on the
                      // same flag as the step-3 income card — promising it here
                      // while step 3 never offers it is a dead end, and prod runs
                      // SHOW_FINANCIAL_ACCOUNTS=false until the launch flip.
                      // Keep the two in lockstep.
                      ...(SHOW_FINANCIAL_ACCOUNTS
                        ? [
                            {
                              icon: ReceiptText,
                              title: "Payroll verification",
                              body: "Connect your payroll provider — it's quick, and you can upload pay stubs instead",
                            },
                          ]
                        : []),
                      {
                        icon: UserPlus,
                        title: "Invite one person to vouch",
                        body: "Ask someone who knows you to confirm they know you",
                      },
                    ]).map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <li key={i} className="flex gap-3">
                          <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 pt-1">
                            <p className="text-sm font-semibold text-foreground">{item.title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{item.body}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ol>

                  {!isViewingPast && (
                    <div className="space-y-4 pt-1">
                      <label className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors">
                        {/* Named explicitly, as on the new look above. */}
                        <Checkbox
                          checked={consentAccepted}
                          onCheckedChange={(v) => setConsentAccepted(v === true)}
                          className="mt-0.5"
                          aria-labelledby="applicant-consent-sentence"
                        />
                        <span id="applicant-consent-sentence" className="text-sm leading-relaxed" style={{ color: "#4b5563" }}>
                          {consentSentence.beforeOrg}{" "}
                          <span className="font-semibold text-foreground">{consentSentence.org}</span>{" "}
                          {consentSentence.afterOrg}{" "}
                          <a href="https://digavouch.com/user-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                            User Policy
                          </a>{" "}
                          and{" "}
                          <a href="https://digavouch.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                            Terms of Service
                          </a>
                        </span>
                      </label>

                      <Button
                        className="w-full h-12 font-semibold text-base"
                        // `nextIndexInPlan`, not a hardcoded 1. The live path
                        // has always asked the plan what follows consent;
                        // demo jumped straight to EMAIL, which is right for
                        // the leasing plan and wrong for any definition that
                        // orders something else second — `tech_role` puts IDV
                        // there, and the demo walked straight past it.
                        onClick={isLive ? handleConsent : () => setCurrentStep(nextIndexInPlan(planIndices, APPLICANT_STEP.CONSENT))}
                        disabled={!consentAccepted || (isLive && consenting)}
                      >
                        {isLive && consenting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                        {checkpoint ? checkpoint.primaryCta : "Accept & Begin"} <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>

                      <button
                        type="button"
                        onClick={() => setDeclineStage("confirm")}
                        className="block w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {checkpoint ? checkpoint.footerNote : "Decline review"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 1: Connect an Email Provider — three layouts based on emailRoute. */}
              {current === 1 && (() => {
                // One evidence value for both email cards (#604): the Flow's
                // server step evidence, or on a tier (leasing) session a real
                // one-time code or proving sign-in, never the leasing-era
                // `stepHasEvidence` heuristic that counts the address itself.
                const emailCardStepSatisfied = applicationEmailStepSatisfied({
                  isLive,
                  definitionDriven: definitionPlan !== null,
                  flowEmailEvidence: stepHasEvidence[APPLICANT_STEP.EMAIL] === true,
                  otpVerifiedCount: verifiedEmailSignals.length,
                  anySignInProvedEmail: anySignInProvedEmail(session),
                });
                const renderProviderRow = (opt: typeof accountOptions[number], opts?: { recommended?: boolean; muted?: boolean }) => {
                  // Demo connection state now lives in FlowContext, same as the
                  // account tiles on the next screen — see handleEmailProviderClick.
                  const selected = !isLive && Boolean(opt.provider) && isConnected(opt.provider!);
                  // Done only when THIS sign-in proved the address. A Microsoft
                  // work sign-in that proved nothing leaves the row usable, so
                  // the applicant can retry with a personal account (platform#712).
                  const connectedLive = isLive && signInProvedEmail(session, opt.provider);
                  const disabled = opt.comingSoon || connectedLive;
                  const liveClick = isLive && opt.provider && !opt.comingSoon && !connectedLive
                    ? () => startOAuth(opt.provider!)
                    : undefined;
                  const isRecommended = Boolean(opts?.recommended) && !connectedLive;
                  return (
                    <div
                      key={opt.name}
                      onClick={() => {
                        if (disabled) return;
                        if (liveClick) return liveClick();
                        if (!isLive) handleEmailProviderClick(opt);
                      }}
                      className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                        connectedLive ? "border-trust-green/40 bg-trust-green-muted/50 cursor-default" :
                        opt.comingSoon ? "border-border bg-muted/30 opacity-70 cursor-not-allowed" :
                        isRecommended ? "border-trust-blue/40 bg-trust-blue-muted/30 hover:bg-trust-blue-muted/50 cursor-pointer" :
                        selected ? "border-trust-green/40 bg-trust-green-muted/50 cursor-pointer" :
                        opts?.muted ? "border-border/60 bg-muted/20 hover:bg-muted/40 cursor-pointer" :
                        "border-border hover:bg-muted/50 cursor-pointer"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-foreground">{opt.name}</p>
                          {opt.comingSoon && <ComingSoonBadge />}
                          {connectedLive && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-trust-green-muted text-trust-green inline-flex items-center gap-1">
                              <Check className="w-2.5 h-2.5" /> Connected
                            </span>
                          )}
                          {isRecommended && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-trust-blue-muted text-trust-blue inline-flex items-center gap-1">
                              <Sparkles className="w-2.5 h-2.5" /> Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{opt.desc}</p>
                      </div>
                      {connectedLive ? <CheckCircle2 className="w-4 h-4 text-trust-green" />
                        : opt.comingSoon ? null
                        : isLive ? <ExternalLink className={`w-4 h-4 ${opts?.muted ? "text-muted-foreground/60" : "text-muted-foreground"}`} />
                        : selected ? <Check className="w-4 h-4 text-trust-green" />
                        : <Plus className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  );
                };

                if (articlePresentation) {
                  const articleEmailCard = applicantEmail
                    ? applicationEmailCard({
                        definitionDriven: definitionPlan !== null,
                        emailStepSatisfied: emailCardStepSatisfied,
                      })
                    : null;
                  const renderArticleProvider = (opt: typeof accountOptions[number]) => {
                    const selected = !isLive && Boolean(opt.provider) && isConnected(opt.provider!);
                    // Same rule as renderProviderRow: done only when THIS sign-in
                    // proved the address, so a Microsoft work sign-in that proved
                    // nothing stays clickable for a retry (platform#712).
                    const connectedLive = isLive && signInProvedEmail(session, opt.provider);
                    const disabled = Boolean(opt.comingSoon || connectedLive);
                    const providerLabel = opt.provider === "microsoft" ? "Microsoft" : opt.name;
                    return (
                      <button
                        key={opt.name}
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                          if (disabled) return;
                          if (isLive && opt.provider) {
                            void startOAuth(opt.provider);
                            return;
                          }
                          handleEmailProviderClick(opt);
                        }}
                        className={`article-choice-row${selected || connectedLive ? " is-connected" : ""}`}
                      >
                        <span className="article-choice-row-main">
                          {opt.provider ? <BrandIcon brand={opt.provider} size={24} /> : <Mail aria-hidden="true" />}
                          <span>{providerLabel}</span>
                        </span>
                        {selected || connectedLive
                          ? <Check className="article-choice-status" aria-label="Connected" />
                          : <ChevronRight aria-hidden="true" />}
                      </button>
                    );
                  };
                  const quickCodePanel = token ? (
                    <EmailCodePanel
                      token={token}
                      onVerified={() => { void refresh(); }}
                      verifiedSignals={applicantEmail
                        ? verifiedEmailSignals.filter(
                            (s) => (s.detail ?? "").toLowerCase() !== applicantEmail.toLowerCase(),
                          )
                        : verifiedEmailSignals}
                      verifiedDisplay="inline"
                      initialEmail={applicantEmail && !articleEmailCard?.claimsVerified ? applicantEmail : undefined}
                    />
                  ) : (
                    <DemoEmailCodePanel
                      presentation={implementationMode ? "implementation" : "demo"}
                      onVerified={(verified) => {
                        connectSource({
                          id: "email",
                          name: verified,
                          category: "other",
                          tenure: "—",
                          corroboration: "Email ownership verified",
                          strength: "moderate",
                        });
                        setShowQuickCode(false);
                      }}
                    />
                  );

                  return (
                    <div className="article-email-step">
                      {applicantEmail && (
                        <div className="article-application-email">
                          <Mail aria-hidden="true" />
                          <div>
                            <span>Application email</span>
                            <strong>{applicantEmail}</strong>
                            {/* States the email STEP, never that this address
                                was verified (#604): the step can be satisfied
                                by a sign-in under another address. */}
                            {articleEmailCard?.note && (
                              <p className="article-application-email-note">{articleEmailCard.note}</p>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="article-choice-list">
                        {visibleAccountOptions.map(renderArticleProvider)}
                      </div>

                      <button
                        type="button"
                        className="article-email-code-link"
                        onClick={() => setShowQuickCode((value) => !value)}
                      >
                        {showQuickCode ? "Hide email code" : "Use an email code"}
                      </button>

                      {showQuickCode && <div className="article-email-code-panel">{quickCodePanel}</div>}

                      <p className="article-email-privacy">
                        <Lock aria-hidden="true" /> We don’t read your messages or store your password.
                      </p>
                    </div>
                  );
                }

                // Primary layout: the application email, then the ways to
                // verify it. The address was typed into the application form
                // and opening the link records nothing, so the card states
                // the address and claims nothing; the code and account rows
                // below are what actually verify it, and they report what
                // the server holds. Applies on every session, leasing
                // included, since platform#604's 2026-09-24 decision — see
                // `applicationEmailCard`'s docblock.
                if (applicantEmail) {
                  const isDefinitionDriven = definitionPlan !== null;
                  const emailCard = applicationEmailCard({
                    definitionDriven: isDefinitionDriven,
                    // ⚠️ NOT `stepHasEvidence[APPLICANT_STEP.EMAIL]` on the
                    // tier (leasing) branch: its `map[1]` counts
                    // `session.applicant_email` itself, the link-click-as-proof
                    // heuristic platform#604 forbids. The shared value above
                    // (`applicationEmailStepSatisfied`) reads it only on a
                    // definition-driven session. See its docblock and the
                    // CLAUDE.md section it points to.
                    emailStepSatisfied: emailCardStepSatisfied,
                  });
                  return (
                    <div className="space-y-5">
                      <PrivacyCallout />

                      {/* Application email card */}
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Your application email</p>
                        <div
                          className={`flex items-center gap-3 p-4 rounded-xl border ${
                            emailCard.claimsVerified
                              ? "border-trust-green/40 bg-trust-green-muted/40"
                              : "border-border bg-card"
                          }`}
                        >
                          <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center shrink-0">
                            <Mail className="w-5 h-5 text-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate">{applicantEmail}</p>
                            {emailCard.claimsVerified ? (
                              <p className="text-xs text-trust-green inline-flex items-center gap-1 mt-0.5">
                                <Check className="w-3 h-3" /> Verified
                              </p>
                            ) : (
                              emailCard.note && (
                                <p className="text-xs text-muted-foreground mt-0.5">{emailCard.note}</p>
                              )
                            )}
                          </div>
                          {emailCard.claimsVerified && (
                            <div className="w-2.5 h-2.5 rounded-full bg-trust-green shrink-0" aria-hidden />
                          )}
                        </div>
                      </div>

                      {/* Ways to verify */}
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{emailCard.optionsLabel}</p>
                        {visibleAccountOptions.map((opt) => renderProviderRow(opt, { muted: true }))}

                        {/* Quick code row — toggles EmailCodePanel below */}
                        <button
                          type="button"
                          onClick={() => setShowQuickCode((v) => !v)}
                          className="w-full flex items-center justify-between p-3 border border-border/60 bg-muted/20 hover:bg-muted/40 rounded-lg transition-colors text-left"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">Quick code</p>
                            <p className="text-xs text-muted-foreground">Send a one-time email code</p>
                          </div>
                          <span className="text-sm font-semibold text-primary shrink-0">
                            {showQuickCode ? "Hide" : "Use code"}
                          </span>
                        </button>

                        {showQuickCode && (
                          <div className="pt-1">
                            {/* `token &&` used to gate this outright, so in demo
                                the row expanded onto nothing — a toggle that
                                only changed its own label. The demo branch
                                walks the same states against a fixed code. */}
                            {token ? (
                              <EmailCodePanel
                                token={token}
                                onVerified={() => { void refresh(); }}
                                verifiedSignals={verifiedEmailSignals.filter(
                                  (s) => (s.detail ?? "").toLowerCase() !== applicantEmail.toLowerCase(),
                                )}
                                verifiedDisplay="inline"
                                // Where the card no longer claims the address
                                // is verified, the code is how to verify it,
                                // so start with it filled in.
                                initialEmail={emailCard.claimsVerified ? undefined : applicantEmail}
                              />
                            ) : (
                              <DemoEmailCodePanel
                                presentation={implementationMode ? "implementation" : "demo"}
                                onVerified={(verified) => {
                                  // id MUST be "email": `verifiedEmailSignals`
                                  // matches on that provider key, and
                                  // NON_OAUTH_ACCOUNT_IDS excludes it from the
                                  // account-check count — an OTP address is not
                                  // an OAuth account check.
                                  connectSource({
                                    id: "email",
                                    name: verified,
                                    category: "other",
                                    tenure: "—",
                                    corroboration: "Email ownership verified",
                                    strength: "moderate",
                                  });
                                  setShowQuickCode(false);
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                // Fallback — no applicant email on file. Show today's OAuth + OTP layout.
                return (
                  <div className="space-y-3">
                    <PrivacyCallout />
                    <div className="space-y-2">
                      {visibleAccountOptions.map((opt) => renderProviderRow(opt))}
                      {isLive && token && (
                        <EmailCodePanel
                          token={token}
                          onVerified={() => { void refresh(); }}
                          verifiedSignals={verifiedEmailSignals}
                          verifiedDisplay="inline"
                        />
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* BF-C1 / ADR-012 R3 — applicant ID verification.
                  Its own section at the BOTTOM of step 1, rendered as a sibling
                  of the layout IIFE above so it appears regardless of which of
                  that block's two branches drew (applicant-email layout vs the
                  no-email fallback) without either having to know about it.

                  Step 1 rather than step 0: step 0 is entirely pre-consent —
                  handleConsent auto-advances to step 1 — and
                  /verify/plaid-idv/link-token 422s CONSENT_REQUIRED without a
                  consent_id, so an IDV button on step 0 is unreachable. Step 1
                  is the first surface where consent exists. Not a new step
                  either: the `steps` indices are load-bearing.

                  Org-gated on applicant_idv_required, so an org that doesn't
                  ask for applicant IDV renders nothing here and its flow is
                  byte-identical to today. Off for every org until BF-B3. */}
              {current === 1 && isLive && effectiveSession?.applicant_idv_required === true && !definitionPlan && (
                <div className="pt-6 mt-6 border-t border-border space-y-3">
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold text-foreground">ID verification</h4>
                    <p className="text-xs text-muted-foreground">
                      {session?.org_name ?? `The ${copy.reviewingTeam}`} asks applicants to confirm
                      their identity with a government ID.
                    </p>
                  </div>
                  <ApplicantIDVCard
                    token={token}
                    // ⚠️ NOT `applicant_idv_verified` alone — same predicate as
                    // the definition-driven card below and as the pip. Here the
                    // flag is never permanently absent (this branch renders only
                    // when `applicant_idv_required` is set, which is what makes
                    // the backend run the lookup), so what this closes is a
                    // window rather than a persistent state: the
                    // `steps_completed` write sits outside the capture
                    // transaction and is allowed to fail, so between a passing
                    // capture and that write the card would offer the button for
                    // an IDV that already succeeded. Cannot overclaim —
                    // `steps_completed` only receives `plaid_idv` on a capture
                    // that PASSED.
                    verified={effectiveSession ? isIdvSatisfied(effectiveSession) : false}
                    skipped={effectiveSession?.applicant_idv_skipped === true}
                    // The UNCREDITED tier, so the card can say "N-1 instead of
                    // N". submission.requiredVouches already has the credit
                    // applied once IDV passes, which would understate the offer.
                    vouchesWithoutIdv={serverRule ? serverRule.requiredVouches : requiredVouchesFor(submission.connectedOAuth)}
                    onChanged={() => { void refresh(); }}
                  />
                </div>
              )}

              {/* Step index 6 — Plaid IDV as its own screen (ADR-014).
                  Reached only from a definition-driven flow, where the
                  definition names `plaid_idv` and decides where in the order it
                  sits. `tech_role` puts it second, immediately after consent,
                  which is the earliest point it can work at all:
                  /verify/plaid-idv/link-token 422s CONSENT_REQUIRED without a
                  consent_id.

                  ⚠️ Out-of-band. The result can arrive from the client, from
                  Plaid's STATUS_UPDATED webhook after the browser closed, or
                  from the reconciliation sweep hours later — and none of those
                  bump `current_step`. `stepHasEvidence[6]` therefore reads the
                  recorded step as well as the verified flag, and the applicant
                  moves on via Continue like any other step. */}
              {current === APPLICANT_STEP.IDV && isLive && (
                <div className="space-y-3">
                  <ApplicantIDVCard
                    token={token}
                    // ⚠️ NOT `applicant_idv_verified` alone. The backend runs
                    // that attestation lookup only when the org config flag
                    // `applicant_idv_required` is set, and a definition-driven
                    // org — the only kind that reaches this screen — requires
                    // IDV through its workflow definition instead and need not
                    // set the flag. Reading it alone rendered an enabled
                    // "Verify your identity" button over an IDV that had
                    // already passed, while the pip beside it was green off
                    // `steps_completed`. Same predicate as the pip now.
                    verified={effectiveSession ? isIdvSatisfied(effectiveSession) : false}
                    skipped={effectiveSession?.applicant_idv_skipped === true}
                    requiredByFlow={!planEntryFor(APPLICANT_STEP.IDV).optional}
                    // No vouch-credit line on this path: the ADR-012 R2 credit
                    // is gated on the org flag, which a definition-driven org
                    // need not set. `requiredByFlow` suppresses the promise.
                    vouchesWithoutIdv={null}
                    onChanged={() => { void refresh(); }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Your ID is checked by Plaid. The {copy.reviewingTeam} sees
                    the result, never the document.
                  </p>
                </div>
              )}

              {/* Step index 6, demo mode — a stand-in for the screen above.
                  `ApplicantIDVCard` renders nothing without a session token
                  (it needs one to mint a Plaid link token), so without this the
                  `tech_role` lane would show a blank second screen.

                  Deliberately NOT wired to Plaid sandbox: that needs a real
                  session the demo has never had. It simulates the RESULT so the
                  six-screen tech_role journey walks end to end, and it says so
                  on screen — an audience should never be unsure which parts of
                  a demo were real. Same copy rules as the real card: no
                  "optional", and none of "fail" / "risk" / "fraud" / "pass". */}
              {/* Same purple treatment the email step uses for "Private by
                  design" — it sits between the step heading and the card
                  below, so the reassurance is read before the action. Rendered
                  for both regimes: it describes what the check does, which
                  does not depend on whether the check is real. */}
              {current === APPLICANT_STEP.IDV && checkpoint && (
                <div className="rounded-lg border border-primary/25 bg-primary/5 p-3 space-y-1 mb-3">
                  {checkpoint.idvNotice.map((line) => (
                    <p key={line} className="text-sm text-foreground/80 leading-relaxed">{line}</p>
                  ))}
                </div>
              )}

              {current === APPLICANT_STEP.IDV && !isLive && (
                <DemoIDVPanel
                  presentation={implementationMode ? "implementation" : "demo"}
                  verified={demoIdvVerified}
                  onVerify={() => setDemoIdvVerified(true)}
                  footnote={
                    <p className="text-xs text-muted-foreground">
                      Your ID is checked by Plaid. The {copy.reviewingTeam} sees
                      the result, never the document.
                    </p>
                  }
                />
              )}

              {/* Step 2: Multi-source connections */}
              {/* Step 3 (index 2): "Add more supporting connections".
                  Everything on this step is optional — Continue is never gated.
                  See CLAUDE.md "Applicant flow — step 3" for the contract. */}
              {current === 2 && (() => {
                // Feature toggles — hidden until these surfaces ship to production.
                // Flip to `true` to re-enable; no other code changes required.
                // See docs/verify-flow-step3-hide-sections.md.
                const SHOW_OTHER_CORROBORATION = false; // Uber + cross-surfaced Google/Microsoft
                // Plaid payroll income — SHOW_FINANCIAL_ACCOUNTS is a build-time
                // flag from @/lib/env (VITE_SHOW_FINANCIAL_ACCOUNTS per Netlify
                // site; defaults on in staging/UAT, off in prod). Pair with the
                // backend's PLAID_INCOME_ENABLED (Terraform) — enable both together.

                // signals_summary keys are underscore-form (backend signalKey()
                // does signalType.replace('.', '_')), e.g. "employment_verification".
                // Each rule always has an entry, so gate on status === "verified"
                // — otherwise the button would flip just because the org has the rule.
                const plaidConnected = (session?.signals_summary ?? []).some(
                  (s) =>
                    /employment[._]verification|identity[._]bank_verified/.test(s.key) &&
                    s.status === "verified",
                );
                // Tracker pips count OAuth account checks only — Plaid and
                // email OTP do not move the tier.
                const totalConnected = accountChecksCount;
                // A Microsoft connection counts only when that sign-in proved
                // the address (platform#712); see signInProvedEmail.
                const emailVerified = isLive
                  ? (verifiedEmailSignals.length > 0 || anySignInProvedEmail(session))
                  : true;

                // Carry every offerable email-step sign-in the applicant has not
                // made into Article's "Other accounts" list. "Made" is the
                // rows' own test: a real sign-in with that provider. A one-time
                // code arrives as `{ provider: "email", masked_identifier:
                // "…@gmail.com" }` (the server's email-verification entries),
                // which is not a Google sign-in, so Google stays on offer for
                // an account step it can satisfy. Apple participates only when
                // the session advertises it, via `visibleAccountOptions`.
                const signedInProviderIds = new Set(
                  visibleAccountOptions
                    .map((option) => option.provider)
                    .filter((provider): provider is NonNullable<typeof provider> =>
                      Boolean(provider) && (isConnected(provider!) || (isLive && liveSocialIds.has(provider!)))),
                );
                const toCrossSource = (option: AccountTile & { provider: NonNullable<AccountTile["provider"]> }): SourceOption => ({
                  id: option.provider,
                  name: option.provider === "microsoft" ? "Microsoft" : option.name,
                  desc: option.desc,
                  category: "other",
                  tenure: "—",
                  corroboration: "Email account ownership",
                  strength: "moderate",
                  tag: "Recommended",
                  liveOAuth: option.provider,
                });
                const hasProvider = (option: AccountTile): option is AccountTile & { provider: NonNullable<AccountTile["provider"]> } =>
                  Boolean(option.provider);
                const crossSurfaced: SourceOption[] = unconnectedEmailProviderOptions(
                  visibleAccountOptions,
                  signedInProviderIds,
                )
                  .filter(hasProvider)
                  .map(toCrossSource);
                // The email-step sign-ins already made, for the new look's
                // list: shown with its "Connected" tick rather than dropped,
                // so a Google sign-in does not just vanish from the screen it
                // was made on (Feather UAT walk, 2026-10-14). The voucher's
                // accounts step already shows a connected account this way.
                const connectedCrossSources: SourceOption[] = visibleAccountOptions
                  .filter(hasProvider)
                  .filter((option) => signedInProviderIds.has(option.provider))
                  .map(toCrossSource);

                // Providers this environment can actually complete a flow for.
                // A `gatedOnAvailability` tile is ABSENT rather than disabled —
                // same rule as the page-2 account tiles — because a tile the
                // backend answers with "not configured" is a dead end.
                //
                // The rule itself lives in `@/lib/offerableSources` so it can be
                // tested without rendering this page.
                //
                // On a definition-driven plan whose digital-account step is
                // REQUIRED, a tile that cannot satisfy it is not offered: the
                // applicant would connect it and still be held at Submit.
                const accountsEntry = definitionPlan?.find((entry) => entry.index === APPLICANT_STEP.ACCOUNTS);
                const requiredStepSatisfiedBy =
                  accountsEntry && accountsEntry.optional !== true ? (accountsEntry.satisfiedBy ?? []) : null;
                const offerable = (sources: SourceOption[]) =>
                  offerableSources(sources, {
                    availableOAuth: effectiveSession?.available_oauth_providers ?? [],
                    requiredStepSatisfiedBy,
                  });

                const effectiveCategories = [
                  {
                    key: "social",
                    label: "Social & Professional Accounts",
                    icon: Globe,
                    // Demo gets one extra tile. See DEMO_ONLY_GITHUB — the
                    // `!isLive` guard is what keeps a connector that does not
                    // exist off a real applicant's screen.
                    sources: offerable(isLive ? socialSources : [...socialSources.filter((s) => s.id !== DEMO_ONLY_GITHUB.id), DEMO_ONLY_GITHUB]),
                  },
                  ...(SHOW_OTHER_CORROBORATION
                    ? [{ key: "other", label: "Other Corroboration Sources", icon: Link2, sources: offerable([...crossSurfaced, ...otherSources]) }]
                    : []),
                ] as const;

                if (articlePresentation) {
                  // Article presents only live connectors. The feather review
                  // route adds a GitHub preview tile to the featured pair.
                  const articleSources = Array.from(
                    new Map(
                      [
                        ...effectiveCategories.flatMap((category) => [...category.sources]),
                        ...crossSurfaced,
                        ...connectedCrossSources,
                      ]
                        // Only the PREVIEW tile is Feather-demo-only; a live
                        // GitHub tile (real sign-in, gated above) passes.
                        .filter((source) => featherPresentation || source !== DEMO_ONLY_GITHUB)
                        .map((source) => [source.id, source]),
                    ).values(),
                  );
                  const featuredOrder: readonly string[] = featherPresentation
                    ? FEATHER_FEATURED_ACCOUNT_IDS
                    : ["linkedin"];
                  const otherOrder: readonly string[] = featherPresentation
                    ? FEATHER_OTHER_ACCOUNT_IDS
                    : ["google", "microsoft", "apple", "amazon", "facebook", "github"];
                  const primarySources = articleSources
                    .filter((source) => featuredOrder.includes(source.id))
                    .sort((a, b) => featuredOrder.indexOf(a.id) - featuredOrder.indexOf(b.id));
                  const secondarySources = articleSources
                    .filter((source) => otherOrder.includes(source.id))
                    .sort((a, b) => otherOrder.indexOf(a.id) - otherOrder.indexOf(b.id));
                  const otherAccountsExpanded = expandedCategories.has("other");
                  const renderArticleSource = (src: SourceOption) => {
                    const liveKey = (src.liveOAuth ?? "").toLowerCase();
                    const connected = isConnected(src.id) || (isLive && Boolean(liveKey) && liveSocialIds.has(liveKey));
                    const disabled = Boolean(src.comingSoon || connected);
                    const brand = resolveBrandKey(src.id);
                    return (
                      <button
                        key={src.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => handleSourceClick(src)}
                        className={`article-choice-row${connected ? " is-connected" : ""}`}
                      >
                        <span className="article-choice-row-main">
                          {connected ? (
                            <Check className="article-choice-status" aria-label="Connected" />
                          ) : src.id === "github" ? (
                            <Github aria-hidden="true" />
                          ) : brand ? (
                            <BrandIcon brand={brand} size={24} />
                          ) : (
                            <Plus aria-hidden="true" />
                          )}
                          <span>{src.name}</span>
                        </span>
                        {connected ? null : <ChevronRight aria-hidden="true" />}
                      </button>
                    );
                  };

                  return (
                    <div className="article-digital-accounts">
                      <div className="article-choice-list">
                        {primarySources.map(renderArticleSource)}
                      </div>

                      {secondarySources.length > 0 && (
                        <div className="article-other-accounts">
                          <button
                            type="button"
                            className="article-other-accounts-toggle"
                            aria-expanded={otherAccountsExpanded}
                            onClick={() => toggleCategory("other")}
                          >
                            {otherAccountsExpanded
                              ? <ChevronDown aria-hidden="true" />
                              : <ChevronRight aria-hidden="true" />}
                            Other accounts
                          </button>
                          {otherAccountsExpanded && (
                            <div className="article-other-accounts-content">
                              <p>These accounts can also help confirm it’s you. We use basic profile details, not your messages or posts.</p>
                              <div className="article-choice-list">
                                {secondarySources.map(renderArticleSource)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    {/* Vertical, not flow. Both sentences are TRUE in both
                        verticals — the vouch count falls with connected
                        accounts wherever the account tier applies
                        (`requiredVouchesFor`; a Flow that states its own
                        number is the exception) and account age is evidence
                        everywhere. What differs is
                        which fact this audience is told first, which is a
                        product choice about the vertical. */}
                    {!articlePresentation && <p className="text-xs text-muted-foreground">
                      {isRecruiting
                        ? "Connect an account that has been yours for a while. A longer history is stronger evidence than a new account."
                        : "You've verified your email. Add accounts here if you want to reduce how many people need to vouch for you."}
                    </p>}

                    {!articlePresentation && showsTrackTracker(flowRule) && <AccountChecksTracker filled={verificationTracksFilled} />}

                    <div className="article-account-categories space-y-2">
                      {effectiveCategories.map((cat) => {
                        const isExpanded = expandedCategories.has(cat.key);
                        const catConnected = cat.sources.filter((s) =>
                          isConnected(s.id) ||
                          (isLive && liveSocialIds.has((s.liveOAuth ?? "").toLowerCase())),
                        ).length;
                        const CatIcon = cat.icon;
                        return (
                          <div key={cat.key} className="article-account-category border border-border rounded-lg overflow-hidden">
                            <button
                              onClick={() => toggleCategory(cat.key)}
                              className="article-account-category-toggle w-full flex items-center justify-between p-3.5 hover:bg-muted/50 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                                  <CatIcon className="w-4 h-4 text-muted-foreground" />
                                </div>
                                <div className="text-left">
                                  <p className="text-sm font-medium text-foreground">{cat.label}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {catConnected > 0
                                      ? `${catConnected} of ${cat.sources.length} connected`
                                      : `${cat.sources.length} available`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {catConnected > 0 && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-trust-green-muted text-trust-green font-medium">
                                    {catConnected} linked
                                  </span>
                                )}
                                {isExpanded
                                  ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                              </div>
                            </button>
                            <AnimatePresence initial={false}>
                              {isExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="article-provider-list px-3 pb-3 space-y-1.5 border-t border-border pt-2">
                                    {cat.sources.map((src) => {
                                      const liveKey = (src.liveOAuth ?? "").toLowerCase();
                                      const connected = isConnected(src.id) || (isLive && liveKey && liveSocialIds.has(liveKey));
                                      const disabled = src.comingSoon;
                                      const isRecommended = src.tag === "Recommended" && !connected && !disabled;
                                      const handleRowClick = () => {
                                        if (disabled) return;
                                        handleSourceClick(src);
                                      };
                                      return (
                                        <div key={src.id} className={`article-provider-row p-2.5 rounded-lg transition-colors ${
                                            connected ? "bg-trust-green-muted/50 border border-trust-green/30"
                                            : disabled ? "bg-muted/30 border border-transparent opacity-70"
                                            : isRecommended ? "bg-primary/5 border border-primary/20 hover:bg-primary/10"
                                            : "hover:bg-muted/50 border border-transparent"
                                          }`}>
                                          <motion.div
                                            whileTap={disabled ? undefined : { scale: 0.98 }}
                                            onClick={handleRowClick}
                                            className={`flex items-center justify-between ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                                          >
                                            <div className="flex items-center gap-2.5 min-w-0">
                                              {(() => {
                                                const brand = resolveBrandKey(src.id);
                                                if (connected) {
                                                  return (
                                                    <div className="w-7 h-7 rounded-full flex items-center justify-center bg-trust-green text-trust-green-foreground">
                                                      <Check className="w-3.5 h-3.5" />
                                                    </div>
                                                  );
                                                }
                                                if (brand && !disabled) {
                                                  return (
                                                    <div className="w-7 h-7 rounded-full flex items-center justify-center bg-muted text-foreground/80">
                                                      <BrandIcon brand={brand} size={14} />
                                                    </div>
                                                  );
                                                }
                                                return (
                                                  <div className="w-7 h-7 rounded-full flex items-center justify-center bg-muted text-muted-foreground">
                                                    <Plus className="w-3 h-3" />
                                                  </div>
                                                );
                                              })()}
                                              <div className="min-w-0">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                  <p className="text-sm font-medium text-foreground">{src.name}</p>
                                                  {disabled && <ComingSoonBadge />}
                                                </div>
                                                <p className="text-xs text-muted-foreground truncate">{src.desc}</p>
                                              </div>
                                            </div>
                                            {!connected && !disabled ? (
                                              <span className="text-[11px] text-muted-foreground shrink-0 whitespace-nowrap">~30 sec</span>
                                            ) : null}
                                          </motion.div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>

                    {/* Financial sub-section — live in sandbox via Plaid.
                        Idle button is the dark CTA; link-token is fetched
                        on click, never on mount.
                        Gated behind SHOW_FINANCIAL_ACCOUNTS — hidden until
                        Plaid ships to production. */}
                    {/* Income / payroll. Suppressed entirely on a definition-driven
                        flow: employment verification is not a step any definition
                        can name — `plaid_income_payroll`/`_bank`/`_document` are
                        registered `out_of_flow`, so they are absent from
                        STEP_REGISTRY and `definitions-parity` would reject a
                        definition naming one (ADR-014 §D9 covers what making it a
                        real step needs).

                        So on a definition path this card is asking for something
                        the flow does not include, inside a step that is about
                        something else. `VITE_SHOW_FINANCIAL_ACCOUNTS=false` on the
                        demo site would also hide it, but a Netlify variable is the
                        wrong place to enforce "render only what the definition
                        names". Both, belt and braces. */}
                    {SHOW_FINANCIAL_ACCOUNTS && !definitionPlan && (
                      <div className="border border-border rounded-lg p-4 bg-muted/20 space-y-3">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-muted-foreground" />
                          <p className="text-sm font-medium text-foreground">Verify your income &amp; employment</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Connect the account where you view your{" "}
                          <span className="font-medium text-foreground">paychecks or pay stubs</span> — for example
                          ADP, Gusto, Workday, or Paychex. It's usually the site or app your employer uses to pay
                          you, which may be different from your work email. This confirms your employer and income,
                          and significantly strengthens your verification.
                        </p>
                        {isLive ? (
                          <>
                            {/* State B — the upload fallback, revealed only AFTER a
                                connect attempt closed without success. Showing it up
                                front would collapse the rescue case into "I'd rather
                                not connect" and steer people onto the weaker signal
                                by default. Once revealed it stays: the applicant is
                                in the fallback context now. */}
                            {docFallbackShown && !plaidConnected ? (
                              <div className="space-y-2">
                                <p className="text-xs text-foreground">
                                  {docFallbackReason === "exit"
                                    ? "Couldn't connect your payroll account? Upload your two most recent pay stubs instead — or last year's W-2 if that's easier to find."
                                    : "Upload your two most recent pay stubs — or last year's W-2 if that's easier to find."}
                                </p>
                                <PlaidLinkButton
                                  token={token}
                                  product="payroll"
                                  mode="document"
                                  connected={plaidConnected}
                                  onSuccess={() => void refresh()}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="w-full justify-center gap-2"
                                  onClick={() => retryConnectRef.current?.()}
                                >
                                  <Briefcase className="w-4 h-4" />
                                  Try connecting again
                                </Button>
                                {/* Headless — the ghost button above drives it. */}
                                <PlaidLinkButton
                                  token={token}
                                  product="payroll"
                                  connected={plaidConnected}
                                  onSuccess={() => void refresh()}
                                  renderTrigger={false}
                                  triggerRef={retryConnectRef}
                                />
                                <p className="text-[11px] text-muted-foreground">
                                  A connected account confirms more than uploaded documents can, so
                                  it's worth another try if you're able.
                                </p>
                              </div>
                            ) : (
                              /* State A — connecting is the recommended path. Visual
                                 weight is the main lever keeping people on the
                                 stronger signal, so the upload alternative stays a
                                 muted text link, never a peer button. */
                              <>
                                <PlaidLinkButton
                                  token={token}
                                  product="payroll"
                                  connected={plaidConnected}
                                  onSuccess={() => void refresh()}
                                  onExitWithoutSuccess={() => revealDocFallback("exit")}
                                />
                                {SHOW_PAYROLL_DOC_FALLBACK && !plaidConnected && (
                                  // Reveals the upload block; it does NOT open
                                  // Plaid Link itself. Opening from here would
                                  // race its own reveal — this subtree unmounts
                                  // the moment the state flips, cancelling the
                                  // in-flight link-token fetch, so the click
                                  // would appear to do nothing. The upload
                                  // button lives in the block below instead.
                                  <button
                                    type="button"
                                    onClick={() => revealDocFallback("chosen")}
                                    className="block w-full text-center text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                                  >
                                    Prefer to upload documents instead?
                                  </button>
                                )}
                              </>
                            )}
                            <p className="text-[11px] text-muted-foreground">
                              🔒 You'll sign in securely through Plaid — DigaVouch never sees your password, and we
                              don't store your pay stubs or move any money. Retrieving your details can take a moment;
                              you can continue and they'll appear automatically.
                            </p>
                            <p className="text-[11px] text-muted-foreground text-center">
                              Powered by Plaid · Bank-grade encryption
                            </p>
                          </>
                        ) : (
                          <p className="text-[11px] text-muted-foreground italic">
                            Plaid Link opens here in the live verify flow.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Step 3: Work / income — hidden as of 2026-05-20 (steps[3].hidden = true).
                  Body kept commented for restoration when payroll/bank connectors ship. */}
              {/* {current === 3 && (
                <div className="space-y-3">
                  <div className="inline-flex items-center gap-1.5 bg-muted px-2.5 py-1 rounded-full text-xs text-muted-foreground font-medium">
                    Optional step
                  </div>
                  {["Payroll provider (e.g., Gusto, ADP)", "Bank account connection"].map((opt) => (
                    <div key={opt} className="flex items-center gap-3 p-3 border border-border rounded-lg bg-muted/30 opacity-70 cursor-not-allowed">
                      <Briefcase className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-foreground flex-1">{opt}</span>
                      <ComingSoonBadge />
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground italic">
                    Work and income connectors are not yet available. You can skip this step.
                  </p>
                </div>
              )} */}

              {/* Step 4: Voucher invite */}
              {current === 4 && (() => {
                // Account-check-driven vouch requirement.
                // 0-1 → 3 people, 2 → 2 people, 3+ → 1 person.
                // Counts OAuth account connections only (google / microsoft /
                // linkedin / facebook). Email OTP and Plaid do NOT count.
                // On a Flow that states its own number, that number;
                // otherwise the tier above, unchanged.
                const requiredVouches = requiredInvites;
                const offersAccounts = offersAccountsForFewerVouches(flowRule, requiredVouches);
                const sentVouches = submission.sentVouchInvites;
                const vouchesNeeded = Math.max(0, requiredVouches - sentVouches);
                const vouchesDone = sentVouches >= requiredVouches;
                // Same predicate as VoucherFlow.tsx's `idvRequiredForOrg` (PR
                // #208 / platform#605) — the applicant and their invited
                // voucher must agree on whether that voucher will actually be
                // asked to verify their identity. Fail-closed to "required"
                // while the session hasn't loaded, same posture as the
                // Step-1 Continue gate. platform#615.
                const idvRequiredForOrg = isLive ? (!session || session.voucher_idv_required === true) : true;
                // Plain remaining-action copy. Never render "Invite 0 of N people…".
                // - vouchesDone → completion message
                // - sentVouches === 0 → "Invite N people who know you" (or 1 person)
                // - partial (0 < sent < required) → "Invite M more {person|people} who {knows|know} you"
                const requirementTitle = vouchesDone
                  ? "You've added enough people. That completes this step."
                  : sentVouches === 0
                    ? requiredVouches === 1
                      ? "Invite 1 person who knows you"
                      : `Invite ${requiredVouches} people who know you`
                    : vouchesNeeded === 1
                      ? "Invite 1 more person who knows you"
                      : `Invite ${vouchesNeeded} more people who know you`;
                const helperCopy = vouchesDone
                  ? evidencePiecesCount === 0
                    ? "You're all set on invites. To submit, connect an account or verify an email address other than your application email."
                    : "You're all set on invites. You can submit when you're ready."
                  : requiredVouches === 1
                    ? idvRequiredForOrg
                      ? "They'll only be asked to confirm they know you and verify their own identity."
                      : "They'll only be asked to confirm they know you."
                    : offersAccounts
                      ? `Adding another account check can reduce how many people you need to invite.`
                      : "Each of them will be asked to confirm they know you.";

                const goToAccountChecks = () => {
                  if (isLive) {
                    setLiveSkippedThrough((prev) => Math.max(prev, 4));
                    setLiveViewStep(2);
                  } else {
                    setCurrentStep(2);
                  }
                  if (typeof window !== "undefined") {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                };
                const scrollToInviteForm = () => {
                  voucherFormApiRef.current?.openForm();
                  voucherFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                };

                if (articlePresentation) {
                  return (
                    <div className="article-invite-step">
                      <div className={`article-invite-requirement${vouchesDone ? " is-complete" : ""}`}>
                        <span className="article-invite-status" aria-hidden="true">
                          {vouchesDone ? <Check /> : <UserPlus />}
                        </span>
                        <div className="article-invite-requirement-text">
                          <p className="article-invite-requirement-title">{requirementTitle}</p>
                          <p className="article-invite-requirement-copy">{helperCopy}</p>
                        </div>
                      </div>

                      {!vouchesDone && offersAccounts && (
                        <button
                          type="button"
                          onClick={goToAccountChecks}
                          className="article-invite-account-link"
                        >
                          Add an account instead
                          <ArrowRight aria-hidden="true" />
                        </button>
                      )}

                      <div className="article-invite-form" ref={voucherFormRef}>
                        <VoucherInviteForm
                          ref={voucherFormApiRef}
                          token={token}
                          maxInvitations={requiredVouches}
                          onInvitationsChange={handleInvitationsChange}
                          recruiting={isRecruiting}
                          applicantEmail={applicantEmail}
                          articlePresentation
                        />
                      </div>
                    </div>
                  );
                }

                return (
                  <>
                    {/* Unified verification tracker — same component, same
                        count source as Steps 3 and 5. */}
                    {!articlePresentation && showsTrackTracker(flowRule) && <AccountChecksTracker filled={verificationTracksFilled} />}

                    {/* Combined requirement + actions card */}
                    <div className="rounded-xl bg-[#F5F3FF] border border-[#DDD6FE] px-4 py-4 flex gap-3">
                      <div className="h-10 w-10 rounded-lg bg-[#7C3AED] flex items-center justify-center shrink-0">
                        <ShieldCheck className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#5B21B6]">{requirementTitle}</p>
                        <p className="text-sm text-[#6D28D9] mt-1 leading-relaxed">{helperCopy}</p>
                        {!vouchesDone && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {sentVouches >= 1 && vouchesNeeded >= 1 && (
                              <button
                                type="button"
                                onClick={scrollToInviteForm}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] px-3 py-2 text-sm font-semibold text-white"
                              >
                                <Plus className="h-4 w-4" /> Invite another person
                              </button>
                            )}
                            {offersAccounts && (
                              <button
                                type="button"
                                onClick={goToAccountChecks}
                                className="inline-flex items-center rounded-lg border border-[#DDD6FE] bg-white px-3 py-2 text-sm font-semibold text-[#5B21B6] hover:bg-[#F5F3FF]"
                              >
                                Add account check instead
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div ref={voucherFormRef}>
                      <VoucherInviteForm ref={voucherFormApiRef} token={token} maxInvitations={requiredVouches} onInvitationsChange={handleInvitationsChange} recruiting={isRecruiting} applicantEmail={applicantEmail} />
                    </div>
                  </>
                );
              })()}

              {/* Step 5: Review — driven by `effectiveSession` so demo mode
                  exercises the same grouped renderer as live. */}
              {current === 5 && effectiveSession && (() => {
                const sess = effectiveSession;

                // The Flow's own number where it states one, else the tier
                // driven by OAuth account checks, unchanged.
                const accountChecks = accountChecksCount;
                const requiredVouches = requiredInvites;
                const sentVouches = submission.sentVouchInvites;
                const vouchesNeeded = Math.max(0, requiredVouches - sentVouches);
                // Whether the readiness line asks for N more invites: by the
                // server's evidence count (#716), the same on both looks.
                const asksForMoreInvites = vouchesNeeded > 0 && evidencePiecesCount > 0;

                // Email row detail — prefer an OAuth provider name, fall
                // back to a verified email signal's masked identifier or the
                // applicant's saved application email.
                // Only a sign-in that proved the address: a Microsoft
                // connection without its proof step proves the account, not
                // the email (platform#712).
                const connectedOAuth = (sess.connected_providers ?? []).find(
                  // One-time codes are the `verifiedEmailSignals` half.
                  (p) => p.provider.toLowerCase() !== "email" && connectionProvedEmail(p),
                );
                // The code's signal detail first, then the address list:
                // the order this row has always read them in.
                const otpSignal = (sess.signals_summary ?? []).find(isOtpEmailSignal);
                const firstCodeDetail = otpSignal ? otpSignal.detail : verifiedEmailSignals[0]?.detail;
                const verifiedEmailDetail =
                  firstCodeDetail ??
                  connectedOAuth?.masked_identifier ??
                  applicantEmail ??
                  null;
                const emailProviderLabel = connectedOAuth
                  ? connectedOAuth.provider.charAt(0).toUpperCase() + connectedOAuth.provider.slice(1).toLowerCase()
                  : null;
                // On a definition-driven session the email row reads the
                // server's own step evidence (the same predicate its gate
                // uses), and never offers the application address as proof:
                // that address was typed into a form, not verified.
                // Live only: the demo has no server step evidence to read.
                const definitionDriven = isLive && definitionPlan !== null;
                const emailVerified = definitionDriven
                  ? stepHasEvidence[APPLICANT_STEP.EMAIL] === true
                  : verifiedEmailSignals.length > 0 || Boolean(connectedOAuth);
                // The Row's own `title` already says "Email verified" — this
                // line names WHICH email and how, once, never restating
                // "Verified" itself (va#177). It used to append a static
                // "Verified from your application" segment here, so an
                // applicant read "Email verified" followed by a detail line
                // that also claimed verification, as if two things had been
                // checked instead of one.
                const emailDoneDetail = definitionDriven
                  ? [emailProviderLabel, firstCodeDetail ?? connectedOAuth?.masked_identifier]
                      .filter(Boolean)
                      .join(" · ") || "Email step complete"
                  : [emailProviderLabel, verifiedEmailDetail]
                      .filter(Boolean)
                      .join(" · ") || "From your application";

                const accountChecksDone = accountChecks > 0;
                const vouchesDone = sentVouches >= requiredVouches;

                // What a Flow session still owes, from the plan the server
                // sent and its step evidence. Only read when the server's gate
                // says not ready, so it cannot contradict the gate's "yes".
                const requiredEntry = (index: number) =>
                  plan.some((entry) => entry.index === index && entry.optional !== true);
                const flowLeft = flowRule
                  ? {
                      emailOutstanding: requiredEntry(APPLICANT_STEP.EMAIL) && !stepHasEvidence[APPLICANT_STEP.EMAIL],
                      accountOutstanding: requiredEntry(APPLICANT_STEP.ACCOUNTS) && !stepHasEvidence[APPLICANT_STEP.ACCOUNTS],
                      idOutstanding:
                        requiredEntry(APPLICANT_STEP.IDV) &&
                        !stepHasEvidence[APPLICANT_STEP.IDV] &&
                        sess.applicant_idv_skipped !== true,
                      vouchesNeeded,
                      vouchesSent: sentVouches,
                    }
                  : null;
                const showAccountRow = flowRule
                  ? plan.some((entry) => entry.index === APPLICANT_STEP.ACCOUNTS)
                  : true;
                const goToStep = (index: number) => {
                  if (isLive) {
                    setLiveSkippedThrough((prev) => laterInPlan(planIndices, prev, current));
                    setLiveViewStep(index);
                  } else {
                    setCurrentStep(index);
                  }
                  if (typeof window !== "undefined") {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                };

                if (articlePresentation) {
                  const readinessNote = submission.applicantSubmitted
                    ? submission.copy.primary
                    : submitReady
                      ? `Everything required is ready to share with ${checkpointOrg}.`
                      : flowLeft
                        ? outstandingBeforeSubmit(flowLeft)
                        : asksForMoreInvites
                          ? `Invite ${vouchesNeeded} more ${vouchesNeeded === 1 ? "person" : "people"} before submitting.`
                          : submission.copy.primary;
                  const ArticleReviewRow = ({
                    done,
                    title,
                    detail,
                    editLabel,
                    step,
                  }: {
                    done: boolean;
                    title: string;
                    detail: string;
                    editLabel: string;
                    step: number;
                  }) => (
                    <div className={`article-review-row${done ? " is-complete" : " is-incomplete"}`} role="listitem">
                      <span className="article-review-status" aria-hidden="true">
                        {done ? <Check /> : <span />}
                      </span>
                      <div className="article-review-row-copy">
                        <p className="article-review-row-title">{title}</p>
                        <p className="article-review-row-detail">{detail}</p>
                      </div>
                      <button
                        type="button"
                        className="article-review-edit"
                        onClick={() => goToStep(step)}
                      >
                        {editLabel}
                      </button>
                    </div>
                  );

                  return (
                    <div className="article-review-step">
                      <div className="article-review-rows" role="list" aria-label="Details you will share">
                        <ArticleReviewRow
                          done={emailVerified}
                          title="Email"
                          detail={emailVerified ? emailDoneDetail : "Not verified yet"}
                          editLabel="Edit email"
                          step={APPLICANT_STEP.EMAIL}
                        />
                        {showAccountRow && (
                          <ArticleReviewRow
                            done={accountChecksDone}
                            title="Connected accounts"
                            detail={accountChecksDone
                              ? `${accountChecks} ${accountChecks === 1 ? "account" : "accounts"} connected`
                              : "No account connected yet"}
                            editLabel="Edit accounts"
                            step={APPLICANT_STEP.ACCOUNTS}
                          />
                        )}
                        <ArticleReviewRow
                          done={vouchesDone}
                          title="People who know you"
                          detail={sentVouches === 0
                            ? "No invitations sent yet"
                            : `${sentVouches} of ${requiredVouches} ${requiredVouches === 1 ? "invitation" : "invitations"} sent`}
                          editLabel="Edit invites"
                          step={APPLICANT_STEP.VOUCH}
                        />
                      </div>
                      <p className={`article-review-readiness${submitReady ? " is-ready" : " is-pending"}`} role="status">
                        {readinessNote}
                      </p>
                    </div>
                  );
                }

                const Row = ({
                  done,
                  title,
                  detail,
                }: {
                  done: boolean;
                  title: string;
                  detail: string;
                }) => (
                  <div
                    className={`flex items-start gap-3 rounded-xl px-4 py-3.5 border ${
                      done
                        ? "bg-[#EAF6DC] border-[#D4E8B8]"
                        : "bg-card border-border"
                    }`}
                  >
                    <div
                      className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                        done ? "bg-white" : "bg-muted"
                      }`}
                    >
                      {done ? (
                        <Check className="w-4 h-4 text-[#7C3AED]" strokeWidth={3} />
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground">{title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{detail}</p>
                    </div>
                  </div>
                );

                return (
                  <div className="space-y-4">
                    {/* Reuse the same verification tracker shown on Step 4 so the
                        applicant has consistent visual context on the review screen.
                        Structure is intentionally identical — tracker restructure is
                        out of scope for this PR. */}
                    {!articlePresentation && showsTrackTracker(flowRule) && <AccountChecksTracker filled={verificationTracksFilled} />}

                    <p className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                      Summary
                    </p>


                    <div className="space-y-2.5">
                      <Row
                        done={emailVerified}
                        title={emailVerified ? "Email verified" : "Email not yet verified"}
                        detail={emailVerified ? emailDoneDetail : "Go back to the email step to verify."}
                      />
                      {showAccountRow && (
                        <Row
                          done={accountChecksDone}
                          title={accountChecksDone ? "Account complete" : "No account checks yet"}
                          detail={
                            accountChecksDone
                              ? `${accountChecks} account ${accountChecks === 1 ? "checked" : "checks"}`
                              : flowRule
                                ? "Go back to the account step to connect one."
                                : "Adding an account reduces how many people need to vouch."
                          }
                        />
                      )}
                      <Row
                        done={vouchesDone}
                        title={vouchesDone ? "People invited to vouch" : "Person invited to vouch"}
                        detail={
                          sentVouches === 0
                            ? "No one invited yet"
                            : `${sentVouches} of ${requiredVouches} ${requiredVouches === 1 ? "invite" : "invites"} sent`
                        }
                      />
                    </div>

                    {/* Gating callout — lavender when more is needed, green
                        when ready, neutral once submitted. Copy comes from
                        the submission matrix so wording stays in lockstep
                        with the rest of the flow. */}
                    {submission.applicantSubmitted ? (
                      <div className="rounded-xl border border-border bg-muted/40 px-4 py-3.5">
                        <p className="text-sm font-semibold text-foreground">
                          {submission.copy.primary}
                        </p>
                        {submission.copy.secondary && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {submission.copy.secondary}
                          </p>
                        )}
                      </div>
                    ) : submitReady ? (
                      checkpoint ? (
                        // Purple, per the v6 review mockup — the green
                        // "ready" treatment is the leasing flow's.
                        <div className="rounded-xl border border-primary/25 bg-primary/5 px-4 py-3.5">
                          <p className="text-sm font-semibold text-primary">{checkpoint.submitNotice.title}</p>
                          <p className="text-sm text-muted-foreground mt-1">{checkpoint.submitNotice.body}</p>
                        </div>
                      ) : (
                      <div className="rounded-xl border border-[#D4E8B8] bg-[#EAF6DC] px-4 py-3.5">
                        <p className="text-sm font-semibold text-[#365b14]">
                          You're ready to submit.
                        </p>
                        <p className="text-sm text-[#437a22] mt-1">
                          {requiredVouches === 1
                            ? "You can submit now. Your application will move to review once your vouch is completed."
                            : "You can submit now. Your application will move to review once your vouches are completed."}
                        </p>
                      </div>
                      )
                    ) : flowLeft ? (
                      <div className="rounded-xl border border-[#DDD6FE] bg-[#F5F3FF] px-4 py-3.5">
                        <p className="text-sm font-semibold text-[#5B21B6]">
                          {outstandingBeforeSubmit(flowLeft)}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                          {flowLeft.emailOutstanding && (
                            <button
                              type="button"
                              onClick={() => goToStep(APPLICANT_STEP.EMAIL)}
                              className="text-sm font-semibold text-[#7C3AED] hover:text-[#6D28D9]"
                            >
                              Go to email step →
                            </button>
                          )}
                          {flowLeft.vouchesNeeded > 0 && (
                            <button
                              type="button"
                              onClick={() => goToStep(APPLICANT_STEP.VOUCH)}
                              className="text-sm font-semibold text-[#7C3AED] hover:text-[#6D28D9]"
                            >
                              Go to vouch step →
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-[#DDD6FE] bg-[#F5F3FF] px-4 py-3.5">
                        <p className="text-sm font-semibold text-[#5B21B6]">
                          {asksForMoreInvites
                            ? `You've invited ${sentVouches} of ${requiredVouches} ${requiredVouches === 1 ? "person" : "people"}. Invite ${vouchesNeeded} more ${vouchesNeeded === 1 ? "person" : "people"} to continue.`
                            : submission.copy.primary}
                        </p>
                        <p className="text-sm text-[#6D28D9] mt-1">
                          {asksForMoreInvites
                            ? `Go back and send ${vouchesNeeded} more ${vouchesNeeded === 1 ? "invite" : "invites"} before submitting.`
                            : evidencePiecesCount === 0
                              ? "Then send the vouch requests shown on the vouch step."
                              : "You can also add another account check to reduce how many vouches are needed."}
                        </p>
                        {!vouchesDone && (
                          <button
                            type="button"
                            onClick={() => setCurrentStep(4)}
                            className="mt-2 text-sm font-semibold text-[#7C3AED] hover:text-[#6D28D9]"
                          >
                            Go to vouch step →
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}


              {/* Continue / Skip
                  - When viewing a past (completed) step in live mode, always show
                    a view-only "Continue" that just advances the local view.
                  - Otherwise hide primary CTA on live Step 0 (handled by consent)
                    and live Step 1 (handled per provider). */}
              {current === 0 && declineStage !== "form" ? null : isViewingPast ? (() => {
                // Even when re-visiting Step 4 as a "past" step, Continue
                // must stay gated on the vouch-invite requirement — backend
                // may have advanced the session past this step, but the
                // applicant still needs the required invites in flight
                // before they can move forward from this view.
                const required = current === 4 ? requiredInvites : 0;
                const step4Blocked =
                  current === 4 && submission.sentVouchInvites < required;
                const blockedReason = continueBlockedReason(step4Blocked, required);
                return (
                  <>
                  <Button
                    onClick={() => {
                      if (step4Blocked) {
                        const needed = required - submission.sentVouchInvites;
                        toast({
                          title: "Send more vouch invites to continue",
                          description:
                            needed === 1
                              ? "Invite 1 more person who knows you before continuing."
                              : `Invite ${needed} more people who know you before continuing.`,
                        });
                        return;
                      }
                      void handleNext();
                    }}
                    disabled={step4Blocked}
                    aria-describedby={blockedReason ? "vouch-continue-reason" : undefined}
                    className={articlePresentation && current === APPLICANT_STEP.VOUCH ? "article-invite-continue w-full" : "w-full"}
                    variant="outline"
                  >
                    Continue to next step <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  {blockedReason}
                  </>
                );
              })() : (
                <>
                  {/* Step 5 (Review & Submit) — matrix copy block + Submit
                      button. Live mode gates Submit on `submission.canSubmit`
                      and posts to the backend; demo mode shows the same
                      dynamic copy but keeps Submit always-enabled so the
                      walkthrough can finish without simulating vouches.
                      See docs/CLAUDE-applicant-submission-eligibility.md. */}
                  {current === 5 && (
                    <>


                      <Button
                        onClick={async () => {
                          if (!isLive) {
                            // Demo: always allow Submit to advance the flow.
                            await handleNext();
                            return;
                          }
                          if (!submitReady) {
                            toast({
                              title: "Not ready yet",
                              description: SUBMISSION_REJECTED_MESSAGE,
                              variant: "destructive",
                            });
                            return;
                          }
                          if (token) {
                            try {
                              await applicantApi.submitSession(token);
                              await refresh();
                            } catch (err) {
                              const apiErr = err as ApiError;
                              // 409 / submission_not_ready — the server rejected
                              // the submit as not ready. Surface it and stop.
                              if (apiErr?.code === "submission_not_ready" || apiErr?.status === 409) {
                                toast({
                                  title: "Not ready yet",
                                  description: SUBMISSION_REJECTED_MESSAGE,
                                  variant: "destructive",
                                });
                                return;
                              }
                              // Any other failure (network, 5xx, 404, …): DO NOT
                              // show the completion screen. Previously this was
                              // swallowed and success shown anyway — the applicant
                              // believed they'd submitted when they hadn't (FB-3).
                              toast({
                                title: "Couldn't submit your application",
                                description: apiErr?.message ?? "Please try again.",
                                variant: "destructive",
                              });
                              return;
                            }
                          }
                          setLiveSubmitted(true);
                        }}
                        // The new look's demo keeps Submit gated on
                        // `submitReady`, like live; the old look's demo stays
                        // always enabled. Intended, not a drift.
                        disabled={articlePresentation ? !submitReady : isLive ? !submitReady : false}
                        className={articlePresentation ? "article-review-submit w-full" : "w-full"}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        {articlePresentation
                          ? submitReady ? `Share with ${checkpointOrg}` : "Complete required steps"
                          : submitReady ? "Submit" : submission.copy.buttonLabel}
                      </Button>
                    </>
                  )}

                  {/* Continue button for steps 2-4 (and demo Step 1). Step 5
                      is handled above (both demo and live). Step 0 is handled
                      separately. Live Step 1 advances per provider row.

                      Step 4 (vouch invites) is gated: Continue is disabled
                      until the applicant has sent the required number of valid
                      invites. On a Flow that states its own number, that
                      number (`requiredInvites`); otherwise the tier comes from
                      `accountChecksCount`: 0-1 OAuth → 3 invites, 2 → 2, 3+ → 1.
                      Skip is hidden entirely on Step 4 — there's no bypass. */}
                  {current !== 0 && current !== 5 && (() => {
                    const required = current === 4 ? requiredInvites : 0;
                    const step4Blocked =
                      current === 4 && submission.sentVouchInvites < required;
                    const blockedReason = continueBlockedReason(step4Blocked, required);
                    return (
                      <>
                      <Button
                        onClick={() => {
                          if (step4Blocked) {
                            const needed = required - submission.sentVouchInvites;
                            toast({
                              title: "Send more vouch invites to continue",
                              description:
                                needed === 1
                                  ? "Invite 1 more person who knows you before continuing."
                                  : `Invite ${needed} more people who know you before continuing.`,
                            });
                            return;
                          }
                          void handleNext();
                        }}
                        disabled={step4Blocked}
                        aria-describedby={blockedReason ? "vouch-continue-reason" : undefined}
                        className={articlePresentation && current === APPLICANT_STEP.VOUCH ? "article-invite-continue w-full" : "w-full"}
                      >
                        Continue
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      {blockedReason}
                      </>
                    );
                  })()}
                  {/* Same rule on every look: a definition's `optional`
                      flag, else the legacy index test. */}
                  {offersSkipStep(planIndices, current, {
                    definitionDriven: definitionPlan !== null,
                    optional: planEntryFor(current).optional,
                  }) && (
                    <Button variant="ghost" onClick={handleNext} className="article-skip-step w-full text-xs text-muted-foreground">
                      Skip this step
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
      {/* Optionality lives here in Patrick's mockups: one quiet line carried
          through every step AFTER consent, rather than a block on page 1.
          It names the CHECKPOINT, never a step inside it — the rule is that
          once the applicant is in, no individual step reads as skippable.
          Absent on page 1 (the consent screen already makes the choice) and
          on the completed screen. */}
      {checkpoint && current !== APPLICANT_STEP.CONSENT && !state.stepUpCompleted && (
        <div className="pt-4 text-center">
          <button
            type="button"
            onClick={exitCheckpoint}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          >
            {checkpoint.footerNote}
          </button>
        </div>
      )}
    </div>
  );
}
